// src/components/accordion.tsx
import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";

// src/lib/utils.ts
import { cx } from "class-variance-authority";
import { twMerge } from "tailwind-merge";
var cn = (...inputs) => twMerge(cx(inputs));

// src/components/accordion.tsx
var Accordion = AccordionPrimitive.Root;
var AccordionItem = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React.createElement(
  AccordionPrimitive.Item,
  {
    ref,
    className: cn("border-b", className),
    ...props
  }
));
AccordionItem.displayName = "AccordionItem";
var AccordionTrigger = React.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ React.createElement(AccordionPrimitive.Header, { className: "flex" }, /* @__PURE__ */ React.createElement(
  AccordionPrimitive.Trigger,
  {
    ref,
    className: cn(
      "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
      className
    ),
    ...props
  },
  children,
  /* @__PURE__ */ React.createElement(ChevronDown, { className: "h-4 w-4 shrink-0 transition-transform duration-200" })
)));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;
var AccordionContent = React.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ React.createElement(
  AccordionPrimitive.Content,
  {
    ref,
    className: "overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
    ...props
  },
  /* @__PURE__ */ React.createElement("div", { className: cn("pb-4 pt-0", className) }, children)
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

// src/components/alert.tsx
import * as React2 from "react";
import { cva } from "class-variance-authority";
var alertVariants = cva(
  "relative w-full rounded-lg border px-4 py-3 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground [&>svg~*]:pl-7",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive: "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var Alert = React2.forwardRef(({ className, variant, ...props }, ref) => /* @__PURE__ */ React2.createElement(
  "div",
  {
    ref,
    role: "alert",
    className: cn(alertVariants({ variant }), className),
    ...props
  }
));
Alert.displayName = "Alert";
var AlertTitle = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React2.createElement(
  "h5",
  {
    ref,
    className: cn("mb-1 font-medium leading-none tracking-tight", className),
    ...props
  }
));
AlertTitle.displayName = "AlertTitle";
var AlertDescription = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React2.createElement(
  "div",
  {
    ref,
    className: cn("text-sm [&_p]:leading-relaxed", className),
    ...props
  }
));
AlertDescription.displayName = "AlertDescription";

// src/components/avatar-stack.tsx
import React4 from "react";

// src/components/initials-avatar.tsx
import React3 from "react";
function getInitials(name) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0];
  if (parts.length === 1 || !first) return (first == null ? void 0 : first.charAt(0).toUpperCase()) ?? "?";
  const last = parts[parts.length - 1];
  return (first.charAt(0) + ((last == null ? void 0 : last.charAt(0)) ?? "")).toUpperCase();
}
function getColorFromName(name) {
  const colors = [
    "bg-blue-500",
    "bg-green-500",
    "bg-purple-500",
    "bg-orange-500",
    "bg-pink-500",
    "bg-teal-500",
    "bg-indigo-500",
    "bg-amber-500",
    "bg-cyan-500",
    "bg-rose-500"
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index] ?? "bg-blue-500";
}
var sizeClasses = {
  sm: "h-6 w-6 text-xs",
  md: "h-8 w-8 text-sm",
  lg: "h-10 w-10 text-base"
};
function InitialsAvatar({ name, size = "md", className }) {
  const initials = getInitials(name);
  const bgColor = getColorFromName(name);
  return /* @__PURE__ */ React3.createElement(
    "div",
    {
      className: cn(
        "flex items-center justify-center rounded-full text-white font-medium",
        sizeClasses[size],
        bgColor,
        className
      ),
      title: name
    },
    initials
  );
}

// src/components/avatar-stack.tsx
var overlapClasses = {
  sm: "-ml-1.5",
  md: "-ml-2",
  lg: "-ml-2.5"
};
var overflowSizeClasses = {
  sm: "h-6 w-6 text-xs",
  md: "h-8 w-8 text-sm",
  lg: "h-10 w-10 text-base"
};
function AvatarStack({
  users,
  maxVisible = 3,
  size = "md",
  onClick,
  className
}) {
  const visibleUsers = users.slice(0, maxVisible);
  const overflowCount = Math.max(0, users.length - maxVisible);
  if (users.length === 0) {
    return null;
  }
  const content = /* @__PURE__ */ React4.createElement("div", { className: cn("flex items-center", className) }, visibleUsers.map((user, index) => /* @__PURE__ */ React4.createElement(
    InitialsAvatar,
    {
      key: user.id,
      name: user.name,
      size,
      className: cn(
        "ring-2 ring-background",
        index > 0 && overlapClasses[size]
      )
    }
  )), overflowCount > 0 && /* @__PURE__ */ React4.createElement(
    "div",
    {
      className: cn(
        "flex items-center justify-center rounded-full bg-muted text-muted-foreground font-medium ring-2 ring-background",
        overflowSizeClasses[size],
        overlapClasses[size]
      )
    },
    "+",
    overflowCount
  ));
  if (onClick) {
    return /* @__PURE__ */ React4.createElement(
      "button",
      {
        type: "button",
        onClick,
        className: "inline-flex cursor-pointer hover:opacity-80 transition-opacity",
        "aria-label": `${users.length} user${users.length === 1 ? "" : "s"} with access`
      },
      content
    );
  }
  return content;
}

// src/components/alert-dialog.tsx
import * as React6 from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";

// src/components/button.tsx
import * as React5 from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva as cva2 } from "class-variance-authority";
var buttonVariants = cva2(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:ring-primary",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground focus-visible:ring-primary",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 focus-visible:ring-primary",
        ghost: "hover:bg-accent hover:text-accent-foreground focus-visible:ring-primary",
        link: "text-primary underline-offset-4 hover:underline focus-visible:ring-primary",
        brand: "bg-brand-primary text-brand-primary-foreground hover:bg-brand-primary/90 focus-visible:ring-brand-primary",
        // Brand-specific styling with brand-colored focus ring
        "brand-secondary": "bg-brand-secondary text-brand-secondary-foreground hover:bg-brand-secondary/90 focus-visible:ring-brand-secondary"
        // Secondary brand styling
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10"
      },
      isBranded: {
        true: "focus-visible:ring-brand-primary",
        false: ""
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      isBranded: false
    }
  }
);
var Button = React5.forwardRef(
  ({ className, variant, size, isBranded, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ React5.createElement(
      Comp,
      {
        className: cn(buttonVariants({ variant, size, isBranded, className })),
        ref,
        ...props
      }
    );
  }
);
Button.displayName = "Button";

// src/components/alert-dialog.tsx
var AlertDialog = AlertDialogPrimitive.Root;
var AlertDialogTrigger = AlertDialogPrimitive.Trigger;
var AlertDialogPortal = AlertDialogPrimitive.Portal;
var AlertDialogOverlay = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React6.createElement(
  AlertDialogPrimitive.Overlay,
  {
    className: cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props,
    ref
  }
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;
var AlertDialogContent = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React6.createElement(AlertDialogPortal, null, /* @__PURE__ */ React6.createElement(AlertDialogOverlay, null), /* @__PURE__ */ React6.createElement(
  AlertDialogPrimitive.Content,
  {
    ref,
    className: cn(
      "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
      className
    ),
    ...props
  }
)));
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;
var AlertDialogHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ React6.createElement(
  "div",
  {
    className: cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    ),
    ...props
  }
);
AlertDialogHeader.displayName = "AlertDialogHeader";
var AlertDialogFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ React6.createElement(
  "div",
  {
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
AlertDialogFooter.displayName = "AlertDialogFooter";
var AlertDialogTitle = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React6.createElement(
  AlertDialogPrimitive.Title,
  {
    ref,
    className: cn("text-lg font-semibold", className),
    ...props
  }
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;
var AlertDialogDescription = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React6.createElement(
  AlertDialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;
var AlertDialogAction = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React6.createElement(
  AlertDialogPrimitive.Action,
  {
    ref,
    className: cn(buttonVariants(), className),
    ...props
  }
));
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;
var AlertDialogCancel = React6.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React6.createElement(
  AlertDialogPrimitive.Cancel,
  {
    ref,
    className: cn(
      buttonVariants({ variant: "outline" }),
      "mt-2 sm:mt-0",
      className
    ),
    ...props
  }
));
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

// src/components/breadcrumb.tsx
import * as React7 from "react";
import { Slot as Slot2 } from "@radix-ui/react-slot";
import { ChevronRight, MoreHorizontal } from "lucide-react";
var Breadcrumb = React7.forwardRef(({ ...props }, ref) => /* @__PURE__ */ React7.createElement("nav", { ref, "aria-label": "breadcrumb", ...props }));
Breadcrumb.displayName = "Breadcrumb";
var BreadcrumbList = React7.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React7.createElement(
  "ol",
  {
    ref,
    className: cn(
      "flex flex-wrap items-center gap-1.5 break-words text-sm text-muted-foreground sm:gap-2.5",
      className
    ),
    ...props
  }
));
BreadcrumbList.displayName = "BreadcrumbList";
var BreadcrumbItem = React7.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React7.createElement(
  "li",
  {
    ref,
    className: cn("inline-flex items-center gap-1.5", className),
    ...props
  }
));
BreadcrumbItem.displayName = "BreadcrumbItem";
var BreadcrumbLink = React7.forwardRef(({ asChild, className, ...props }, ref) => {
  const Comp = asChild ? Slot2 : "a";
  return /* @__PURE__ */ React7.createElement(
    Comp,
    {
      ref,
      className: cn("transition-colors hover:text-foreground", className),
      ...props
    }
  );
});
BreadcrumbLink.displayName = "BreadcrumbLink";
var BreadcrumbPage = React7.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React7.createElement(
  "span",
  {
    ref,
    role: "link",
    "aria-disabled": "true",
    "aria-current": "page",
    className: cn("font-normal text-foreground", className),
    ...props
  }
));
BreadcrumbPage.displayName = "BreadcrumbPage";
var BreadcrumbSeparator = ({
  children,
  className,
  ...props
}) => /* @__PURE__ */ React7.createElement(
  "li",
  {
    role: "presentation",
    "aria-hidden": "true",
    className: cn("[&>svg]:w-3.5 [&>svg]:h-3.5", className),
    ...props
  },
  children ?? /* @__PURE__ */ React7.createElement(ChevronRight, null)
);
BreadcrumbSeparator.displayName = "BreadcrumbSeparator";
var BreadcrumbEllipsis = ({
  className,
  ...props
}) => /* @__PURE__ */ React7.createElement(
  "span",
  {
    role: "presentation",
    "aria-hidden": "true",
    className: cn("flex h-9 w-9 items-center justify-center", className),
    ...props
  },
  /* @__PURE__ */ React7.createElement(MoreHorizontal, { className: "h-4 w-4" }),
  /* @__PURE__ */ React7.createElement("span", { className: "sr-only" }, "More")
);
BreadcrumbEllipsis.displayName = "BreadcrumbElipssis";

// src/components/badge.tsx
import * as React8 from "react";
import { cva as cva3 } from "class-variance-authority";
var badgeVariants = cva3(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive: "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        brand: "border-transparent bg-brand-primary text-brand-primary-foreground hover:bg-brand-primary/80",
        "brand-secondary": "border-transparent bg-brand-secondary text-brand-secondary-foreground hover:bg-brand-secondary/80"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Badge({ className, variant, isBranded, ...props }) {
  const finalVariant = isBranded ? "brand" : variant;
  return /* @__PURE__ */ React8.createElement("div", { className: cn(badgeVariants({ variant: finalVariant }), className), ...props });
}

// src/components/calendar.tsx
import * as React9 from "react";
import { DayPicker } from "react-day-picker";
import { ChevronLeft, ChevronRight as ChevronRight2 } from "lucide-react";
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}) {
  return /* @__PURE__ */ React9.createElement(
    DayPicker,
    {
      showOutsideDays,
      className: cn("p-3", className),
      classNames: {
        months: "relative flex flex-col gap-y-4 sm:flex-row sm:gap-x-4 sm:gap-y-0",
        month_caption: "flex h-7 items-center justify-center",
        caption_label: "text-sm font-medium",
        nav: "flex items-center gap-1 absolute top-0 inset-x-0 justify-between z-10",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "size-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "size-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        month_grid: "mx-auto",
        weekdays: "flex",
        weekday: "w-8 text-[0.8rem] font-normal text-muted-foreground",
        week: "flex mt-2",
        day: cn(
          "relative p-0 text-center text-sm",
          "[&:has([aria-selected])]:rounded-md"
        ),
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "size-8 p-0 font-normal aria-selected:opacity-100"
        ),
        selected: "bg-primary text-primary-foreground *:hover:bg-primary *:hover:text-primary-foreground *:focus:bg-primary *:focus:text-primary-foreground",
        today: "bg-accent text-accent-foreground",
        outside: "text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        disabled: "text-muted-foreground opacity-50",
        hidden: "invisible",
        ...classNames
      },
      components: {
        Chevron: ({ orientation }) => {
          const Icon2 = orientation === "left" ? ChevronLeft : ChevronRight2;
          return /* @__PURE__ */ React9.createElement(Icon2, { className: "size-4" });
        }
      },
      ...props
    }
  );
}
Calendar.displayName = "Calendar";

// src/components/card.tsx
import * as React10 from "react";
var Card = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React10.createElement(
  "div",
  {
    ref,
    className: cn(
      "rounded-lg border bg-card text-card-foreground shadow-sm",
      className
    ),
    ...props
  }
));
Card.displayName = "Card";
var CardHeader = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React10.createElement(
  "div",
  {
    ref,
    className: cn("flex flex-col space-y-1.5 p-6", className),
    ...props
  }
));
CardHeader.displayName = "CardHeader";
var CardTitle = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React10.createElement(
  "h3",
  {
    ref,
    className: cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
CardTitle.displayName = "CardTitle";
var CardDescription = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React10.createElement(
  "p",
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
CardDescription.displayName = "CardDescription";
var CardContent = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React10.createElement("div", { ref, className: cn("p-6 pt-0", className), ...props }));
CardContent.displayName = "CardContent";
var CardFooter = React10.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React10.createElement(
  "div",
  {
    ref,
    className: cn("flex items-center p-6 pt-0", className),
    ...props
  }
));
CardFooter.displayName = "CardFooter";

// src/components/checkbox.tsx
import * as React11 from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";
var Checkbox = React11.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React11.createElement(
  CheckboxPrimitive.Root,
  {
    ref,
    className: cn(
      "peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
      className
    ),
    ...props
  },
  /* @__PURE__ */ React11.createElement(
    CheckboxPrimitive.Indicator,
    {
      className: cn("flex items-center justify-center text-current")
    },
    /* @__PURE__ */ React11.createElement(Check, { className: "h-4 w-4" })
  )
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

// src/components/collapsible.tsx
import * as React12 from "react";
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
var Collapsible = CollapsiblePrimitive.Root;
var CollapsibleTrigger = CollapsiblePrimitive.Trigger;
var CollapsibleContent = React12.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ React12.createElement(
  CollapsiblePrimitive.Content,
  {
    ref,
    className: cn(
      "overflow-hidden transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
      className
    ),
    ...props
  },
  children
));
CollapsibleContent.displayName = "CollapsibleContent";

// src/components/dialog.tsx
import * as React13 from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
var Dialog = DialogPrimitive.Root;
var DialogTrigger = DialogPrimitive.Trigger;
var DialogPortal = ({ container, ...props }) => /* @__PURE__ */ React13.createElement(
  DialogPrimitive.Portal,
  {
    container,
    ...props
  }
);
DialogPortal.displayName = "DialogPortal";
var DialogClose = DialogPrimitive.Close;
var DialogOverlay = React13.forwardRef(({ className, isContained = false, ...props }, ref) => /* @__PURE__ */ React13.createElement(
  DialogPrimitive.Overlay,
  {
    ref,
    className: cn(
      // Use absolute positioning when contained in Shadow DOM, fixed when not
      isContained ? "absolute inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 modal-overlay" : "fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props
  }
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
var DialogContent = React13.forwardRef(({ className, children, container, showCloseButton = true, ...props }, ref) => {
  const isContained = !!container;
  return /* @__PURE__ */ React13.createElement(DialogPortal, { container }, /* @__PURE__ */ React13.createElement(DialogOverlay, { isContained }), /* @__PURE__ */ React13.createElement(
    DialogPrimitive.Content,
    {
      ref,
      className: cn(
        // Use absolute positioning when contained in Shadow DOM
        isContained ? "absolute left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg" : "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      ),
      ...props
    },
    children,
    showCloseButton && /* @__PURE__ */ React13.createElement(DialogPrimitive.Close, { className: "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground" }, /* @__PURE__ */ React13.createElement(X, { className: "h-4 w-4" }), /* @__PURE__ */ React13.createElement("span", { className: "sr-only" }, "Close"))
  ));
});
DialogContent.displayName = DialogPrimitive.Content.displayName;
var DialogHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ React13.createElement(
  "div",
  {
    className: cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    ),
    ...props
  }
);
DialogHeader.displayName = "DialogHeader";
var DialogFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ React13.createElement(
  "div",
  {
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
DialogFooter.displayName = "DialogFooter";
var DialogTitle = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React13.createElement(
  DialogPrimitive.Title,
  {
    ref,
    className: cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
var DialogDescription = React13.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React13.createElement(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

// src/components/drawer.tsx
import * as React14 from "react";
import { Drawer as DrawerPrimitive } from "vaul";
var Drawer = ({
  shouldScaleBackground = true,
  ...props
}) => /* @__PURE__ */ React14.createElement(
  DrawerPrimitive.Root,
  {
    shouldScaleBackground,
    ...props
  }
);
Drawer.displayName = "Drawer";
var DrawerTrigger = DrawerPrimitive.Trigger;
var DrawerPortal = ({ container, ...props }) => /* @__PURE__ */ React14.createElement(
  DrawerPrimitive.Portal,
  {
    container,
    ...props
  }
);
DrawerPortal.displayName = "DrawerPortal";
var DrawerClose = DrawerPrimitive.Close;
var DrawerOverlay = React14.forwardRef(({ className, isContained = false, ...props }, ref) => /* @__PURE__ */ React14.createElement(
  DrawerPrimitive.Overlay,
  {
    ref,
    className: cn(
      isContained ? "absolute inset-0 z-50 bg-black/80" : "fixed inset-0 z-50 bg-black/80",
      className
    ),
    ...props
  }
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;
var DrawerContent = React14.forwardRef(({ className, children, container, ...props }, ref) => {
  const isContained = !!container;
  return /* @__PURE__ */ React14.createElement(DrawerPortal, { container }, /* @__PURE__ */ React14.createElement(DrawerOverlay, { isContained }), /* @__PURE__ */ React14.createElement(
    DrawerPrimitive.Content,
    {
      ref,
      className: cn(
        isContained ? "absolute inset-x-0 bottom-0 z-50 mt-24 flex h-auto flex-col rounded-t-[10px] border bg-background" : "fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto flex-col rounded-t-[10px] border bg-background",
        className
      ),
      ...props
    },
    /* @__PURE__ */ React14.createElement("div", { className: "mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" }),
    children
  ));
});
DrawerContent.displayName = "DrawerContent";
var DrawerHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ React14.createElement(
  "div",
  {
    className: cn("grid gap-1.5 p-4 text-center sm:text-left", className),
    ...props
  }
);
DrawerHeader.displayName = "DrawerHeader";
var DrawerFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ React14.createElement(
  "div",
  {
    className: cn("mt-auto flex flex-col gap-2 p-4", className),
    ...props
  }
);
DrawerFooter.displayName = "DrawerFooter";
var DrawerTitle = React14.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React14.createElement(
  DrawerPrimitive.Title,
  {
    ref,
    className: cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;
var DrawerDescription = React14.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React14.createElement(
  DrawerPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

// src/components/dropdown-menu.tsx
import * as React15 from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check as Check2, ChevronRight as ChevronRight3, Circle } from "lucide-react";
var DropdownMenu = DropdownMenuPrimitive.Root;
var DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
var DropdownMenuGroup = DropdownMenuPrimitive.Group;
var DropdownMenuPortal = DropdownMenuPrimitive.Portal;
var DropdownMenuSub = DropdownMenuPrimitive.Sub;
var DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;
var DropdownMenuSubTrigger = React15.forwardRef(({ className, inset, children, ...props }, ref) => /* @__PURE__ */ React15.createElement(
  DropdownMenuPrimitive.SubTrigger,
  {
    ref,
    className: cn(
      "flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
      inset && "pl-8",
      className
    ),
    ...props
  },
  children,
  /* @__PURE__ */ React15.createElement(ChevronRight3, { className: "ml-auto" })
));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
var DropdownMenuSubContent = React15.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React15.createElement(
  DropdownMenuPrimitive.SubContent,
  {
    ref,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-dropdown-menu-content-transform-origin]",
      className
    ),
    ...props
  }
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;
var DropdownMenuContent = React15.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ React15.createElement(DropdownMenuPrimitive.Portal, null, /* @__PURE__ */ React15.createElement(
  DropdownMenuPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-dropdown-menu-content-transform-origin]",
      className
    ),
    ...props
  }
)));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
var DropdownMenuItem = React15.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ React15.createElement(
  DropdownMenuPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
var DropdownMenuCheckboxItem = React15.forwardRef(({ className, children, checked, ...props }, ref) => /* @__PURE__ */ React15.createElement(
  DropdownMenuPrimitive.CheckboxItem,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    checked,
    ...props
  },
  /* @__PURE__ */ React15.createElement("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center" }, /* @__PURE__ */ React15.createElement(DropdownMenuPrimitive.ItemIndicator, null, /* @__PURE__ */ React15.createElement(Check2, { className: "h-4 w-4" }))),
  children
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;
var DropdownMenuRadioItem = React15.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ React15.createElement(
  DropdownMenuPrimitive.RadioItem,
  {
    ref,
    className: cn(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props
  },
  /* @__PURE__ */ React15.createElement("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center" }, /* @__PURE__ */ React15.createElement(DropdownMenuPrimitive.ItemIndicator, null, /* @__PURE__ */ React15.createElement(Circle, { className: "h-2 w-2 fill-current" }))),
  children
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;
var DropdownMenuLabel = React15.forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ React15.createElement(
  DropdownMenuPrimitive.Label,
  {
    ref,
    className: cn(
      "px-2 py-1.5 text-sm font-semibold",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
var DropdownMenuSeparator = React15.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React15.createElement(
  DropdownMenuPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-muted", className),
    ...props
  }
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
var DropdownMenuShortcut = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ React15.createElement(
    "span",
    {
      className: cn("ml-auto text-xs tracking-widest opacity-60", className),
      ...props
    }
  );
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

// src/components/dual-listbox.tsx
import * as React18 from "react";
import { ChevronRight as ChevronRight4, ChevronLeft as ChevronLeft2, ChevronsRight, ChevronsLeft, Search } from "lucide-react";

// src/components/input.tsx
import * as React16 from "react";
var Input = React16.forwardRef(
  ({ className, type, isBranded = false, ...props }, ref) => {
    const brandedClasses = isBranded ? "focus-visible:ring-brand-primary" : "focus-visible:ring-ring";
    return /* @__PURE__ */ React16.createElement(
      "input",
      {
        type,
        className: cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          brandedClasses,
          className
        ),
        ref,
        ...props
      }
    );
  }
);
Input.displayName = "Input";

// src/components/scroll-area.tsx
import * as React17 from "react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
var ScrollArea = React17.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ React17.createElement(
  ScrollAreaPrimitive.Root,
  {
    ref,
    className: cn("relative overflow-hidden", className),
    ...props
  },
  /* @__PURE__ */ React17.createElement(ScrollAreaPrimitive.Viewport, { className: "h-full w-full rounded-[inherit]" }, children),
  /* @__PURE__ */ React17.createElement(ScrollBar, null),
  /* @__PURE__ */ React17.createElement(ScrollAreaPrimitive.Corner, null)
));
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;
var ScrollBar = React17.forwardRef(({ className, orientation = "vertical", ...props }, ref) => /* @__PURE__ */ React17.createElement(
  ScrollAreaPrimitive.ScrollAreaScrollbar,
  {
    ref,
    orientation,
    className: cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className
    ),
    ...props
  },
  /* @__PURE__ */ React17.createElement(ScrollAreaPrimitive.ScrollAreaThumb, { className: "relative flex-1 rounded-full bg-border" })
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

// src/components/dual-listbox.tsx
function ListItem({ item, isSelected, onToggle, showImage }) {
  return /* @__PURE__ */ React18.createElement(
    "div",
    {
      onClick: onToggle,
      className: cn(
        "flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
        isSelected ? "bg-primary/10" : "hover:bg-muted"
      )
    },
    /* @__PURE__ */ React18.createElement(
      Checkbox,
      {
        checked: isSelected,
        onCheckedChange: onToggle,
        onClick: (e) => e.stopPropagation()
      }
    ),
    showImage && item.imageUrl && /* @__PURE__ */ React18.createElement(
      "img",
      {
        src: item.imageUrl,
        alt: "",
        className: "h-6 w-6 flex-shrink-0 rounded object-contain"
      }
    ),
    /* @__PURE__ */ React18.createElement("div", { className: "min-w-0 flex-1 overflow-hidden" }, /* @__PURE__ */ React18.createElement("div", { className: "truncate font-medium" }, item.label), item.description && /* @__PURE__ */ React18.createElement("div", { className: "line-clamp-2 text-xs text-muted-foreground break-words" }, item.description))
  );
}
function DualListbox({
  available,
  assigned,
  onChange,
  availableLabel = "Available",
  assignedLabel = "Assigned",
  searchPlaceholder = "Search...",
  height = "300px",
  showImages = false,
  className
}) {
  const [availableSearch, setAvailableSearch] = React18.useState("");
  const [assignedSearch, setAssignedSearch] = React18.useState("");
  const [selectedAvailable, setSelectedAvailable] = React18.useState(/* @__PURE__ */ new Set());
  const [selectedAssigned, setSelectedAssigned] = React18.useState(/* @__PURE__ */ new Set());
  const assignedIds = React18.useMemo(
    () => new Set(assigned.map((item) => item.id)),
    [assigned]
  );
  const filteredAvailable = React18.useMemo(() => {
    return available.filter((item) => !assignedIds.has(item.id)).filter(
      (item) => {
        var _a;
        return item.label.toLowerCase().includes(availableSearch.toLowerCase()) || ((_a = item.description) == null ? void 0 : _a.toLowerCase().includes(availableSearch.toLowerCase()));
      }
    );
  }, [available, assignedIds, availableSearch]);
  const filteredAssigned = React18.useMemo(() => {
    return assigned.filter(
      (item) => {
        var _a;
        return item.label.toLowerCase().includes(assignedSearch.toLowerCase()) || ((_a = item.description) == null ? void 0 : _a.toLowerCase().includes(assignedSearch.toLowerCase()));
      }
    );
  }, [assigned, assignedSearch]);
  const toggleAvailableSelection = React18.useCallback((id) => {
    setSelectedAvailable((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);
  const toggleAssignedSelection = React18.useCallback((id) => {
    setSelectedAssigned((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);
  const selectAllAvailable = React18.useCallback(() => {
    setSelectedAvailable(new Set(filteredAvailable.map((item) => item.id)));
  }, [filteredAvailable]);
  const selectAllAssigned = React18.useCallback(() => {
    setSelectedAssigned(new Set(filteredAssigned.map((item) => item.id)));
  }, [filteredAssigned]);
  const moveToAssigned = React18.useCallback(() => {
    const newAssignedIds = [...assignedIds, ...selectedAvailable];
    onChange(newAssignedIds);
    setSelectedAvailable(/* @__PURE__ */ new Set());
  }, [assignedIds, selectedAvailable, onChange]);
  const moveAllToAssigned = React18.useCallback(() => {
    const newAssignedIds = [
      ...assignedIds,
      ...filteredAvailable.map((item) => item.id)
    ];
    onChange(newAssignedIds);
    setSelectedAvailable(/* @__PURE__ */ new Set());
  }, [assignedIds, filteredAvailable, onChange]);
  const moveToAvailable = React18.useCallback(() => {
    const newAssignedIds = [...assignedIds].filter(
      (id) => !selectedAssigned.has(id)
    );
    onChange(newAssignedIds);
    setSelectedAssigned(/* @__PURE__ */ new Set());
  }, [assignedIds, selectedAssigned, onChange]);
  const moveAllToAvailable = React18.useCallback(() => {
    const assignedInFilter = new Set(filteredAssigned.map((item) => item.id));
    const newAssignedIds = [...assignedIds].filter(
      (id) => !assignedInFilter.has(id)
    );
    onChange(newAssignedIds);
    setSelectedAssigned(/* @__PURE__ */ new Set());
  }, [assignedIds, filteredAssigned, onChange]);
  return /* @__PURE__ */ React18.createElement("div", { className: cn("flex gap-4 overflow-hidden", className) }, /* @__PURE__ */ React18.createElement("div", { className: "flex-1 space-y-2" }, /* @__PURE__ */ React18.createElement("div", { className: "flex items-center justify-between" }, /* @__PURE__ */ React18.createElement("span", { className: "text-sm font-medium" }, availableLabel), /* @__PURE__ */ React18.createElement(
    Button,
    {
      variant: "ghost",
      size: "sm",
      onClick: selectAllAvailable,
      disabled: filteredAvailable.length === 0,
      className: "h-6 text-xs"
    },
    "Select all"
  )), /* @__PURE__ */ React18.createElement("div", { className: "relative" }, /* @__PURE__ */ React18.createElement(Search, { className: "absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" }), /* @__PURE__ */ React18.createElement(
    Input,
    {
      placeholder: searchPlaceholder,
      value: availableSearch,
      onChange: (e) => setAvailableSearch(e.target.value),
      className: "pl-9"
    }
  )), /* @__PURE__ */ React18.createElement("div", { className: "rounded-md border", style: { height } }, /* @__PURE__ */ React18.createElement(ScrollArea, { className: "h-full" }, /* @__PURE__ */ React18.createElement("div", { className: "space-y-1 p-2" }, filteredAvailable.length === 0 ? /* @__PURE__ */ React18.createElement("div", { className: "py-8 text-center text-sm text-muted-foreground" }, availableSearch ? "No items match search" : "No items available") : filteredAvailable.map((item) => /* @__PURE__ */ React18.createElement(
    ListItem,
    {
      key: item.id,
      item,
      isSelected: selectedAvailable.has(item.id),
      onToggle: () => toggleAvailableSelection(item.id),
      showImage: showImages
    }
  ))))), /* @__PURE__ */ React18.createElement("div", { className: "text-center text-xs text-muted-foreground" }, selectedAvailable.size > 0 && `${selectedAvailable.size} selected of `, filteredAvailable.length, " item", filteredAvailable.length !== 1 ? "s" : "")), /* @__PURE__ */ React18.createElement("div", { className: "flex flex-col items-center justify-center gap-2" }, /* @__PURE__ */ React18.createElement(
    Button,
    {
      variant: "outline",
      size: "sm",
      onClick: moveAllToAssigned,
      disabled: filteredAvailable.length === 0,
      title: "Move all to assigned"
    },
    /* @__PURE__ */ React18.createElement(ChevronsRight, { className: "h-4 w-4" })
  ), /* @__PURE__ */ React18.createElement(
    Button,
    {
      variant: "outline",
      size: "sm",
      onClick: moveToAssigned,
      disabled: selectedAvailable.size === 0,
      title: "Move selected to assigned"
    },
    /* @__PURE__ */ React18.createElement(ChevronRight4, { className: "h-4 w-4" })
  ), /* @__PURE__ */ React18.createElement(
    Button,
    {
      variant: "outline",
      size: "sm",
      onClick: moveToAvailable,
      disabled: selectedAssigned.size === 0,
      title: "Move selected to available"
    },
    /* @__PURE__ */ React18.createElement(ChevronLeft2, { className: "h-4 w-4" })
  ), /* @__PURE__ */ React18.createElement(
    Button,
    {
      variant: "outline",
      size: "sm",
      onClick: moveAllToAvailable,
      disabled: filteredAssigned.length === 0,
      title: "Move all to available"
    },
    /* @__PURE__ */ React18.createElement(ChevronsLeft, { className: "h-4 w-4" })
  )), /* @__PURE__ */ React18.createElement("div", { className: "flex-1 space-y-2" }, /* @__PURE__ */ React18.createElement("div", { className: "flex items-center justify-between" }, /* @__PURE__ */ React18.createElement("span", { className: "text-sm font-medium" }, assignedLabel), /* @__PURE__ */ React18.createElement(
    Button,
    {
      variant: "ghost",
      size: "sm",
      onClick: selectAllAssigned,
      disabled: filteredAssigned.length === 0,
      className: "h-6 text-xs"
    },
    "Select all"
  )), /* @__PURE__ */ React18.createElement("div", { className: "relative" }, /* @__PURE__ */ React18.createElement(Search, { className: "absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" }), /* @__PURE__ */ React18.createElement(
    Input,
    {
      placeholder: searchPlaceholder,
      value: assignedSearch,
      onChange: (e) => setAssignedSearch(e.target.value),
      className: "pl-9"
    }
  )), /* @__PURE__ */ React18.createElement("div", { className: "rounded-md border", style: { height } }, /* @__PURE__ */ React18.createElement(ScrollArea, { className: "h-full" }, /* @__PURE__ */ React18.createElement("div", { className: "space-y-1 p-2" }, filteredAssigned.length === 0 ? /* @__PURE__ */ React18.createElement("div", { className: "py-8 text-center text-sm text-muted-foreground" }, assignedSearch ? "No items match search" : "No items assigned") : filteredAssigned.map((item) => /* @__PURE__ */ React18.createElement(
    ListItem,
    {
      key: item.id,
      item,
      isSelected: selectedAssigned.has(item.id),
      onToggle: () => toggleAssignedSelection(item.id),
      showImage: showImages
    }
  ))))), /* @__PURE__ */ React18.createElement("div", { className: "text-center text-xs text-muted-foreground" }, selectedAssigned.size > 0 && `${selectedAssigned.size} selected of `, filteredAssigned.length, " item", filteredAssigned.length !== 1 ? "s" : "")));
}

// src/components/form.tsx
import * as React20 from "react";
import { Slot as Slot3 } from "@radix-ui/react-slot";
import {
  Controller,
  FormProvider,
  useFormContext,
  useForm
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

// src/components/label.tsx
import * as React19 from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cva as cva4 } from "class-variance-authority";
var labelVariants = cva4(
  "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
);
var Label2 = React19.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React19.createElement(
  LabelPrimitive.Root,
  {
    ref,
    className: cn(labelVariants(), className),
    ...props
  }
));
Label2.displayName = LabelPrimitive.Root.displayName;

// src/components/form.tsx
var Form = FormProvider;
var FormFieldContext = React20.createContext(
  {}
);
var FormField = ({
  ...props
}) => {
  return /* @__PURE__ */ React20.createElement(FormFieldContext.Provider, { value: { name: props.name } }, /* @__PURE__ */ React20.createElement(Controller, { ...props }));
};
var useFormField = () => {
  const fieldContext = React20.useContext(FormFieldContext);
  const itemContext = React20.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();
  const fieldState = getFieldState(fieldContext.name, formState);
  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }
  const { id } = itemContext;
  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState
  };
};
var FormItemContext = React20.createContext(
  {}
);
var FormItem = React20.forwardRef(({ className, ...props }, ref) => {
  const id = React20.useId();
  return /* @__PURE__ */ React20.createElement(FormItemContext.Provider, { value: { id } }, /* @__PURE__ */ React20.createElement("div", { ref, className: cn("space-y-2", className), ...props }));
});
FormItem.displayName = "FormItem";
var FormLabel = React20.forwardRef(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField();
  return /* @__PURE__ */ React20.createElement(
    Label2,
    {
      ref,
      className: cn(error && "text-destructive", className),
      htmlFor: formItemId,
      ...props
    }
  );
});
FormLabel.displayName = "FormLabel";
var FormControl = React20.forwardRef(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
  return /* @__PURE__ */ React20.createElement(
    Slot3,
    {
      ref,
      id: formItemId,
      "aria-describedby": !error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`,
      "aria-invalid": !!error,
      ...props
    }
  );
});
FormControl.displayName = "FormControl";
var FormDescription = React20.forwardRef(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField();
  return /* @__PURE__ */ React20.createElement(
    "p",
    {
      ref,
      id: formDescriptionId,
      className: cn("text-sm text-muted-foreground", className),
      ...props
    }
  );
});
FormDescription.displayName = "FormDescription";
var FormMessage = React20.forwardRef(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField();
  const body = error ? String((error == null ? void 0 : error.message) ?? "") : children;
  if (!body) {
    return null;
  }
  return /* @__PURE__ */ React20.createElement(
    "p",
    {
      ref,
      id: formMessageId,
      className: cn("text-sm font-medium text-destructive", className),
      ...props
    },
    body
  );
});
FormMessage.displayName = "FormMessage";

// src/components/hover-card.tsx
import * as React21 from "react";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";
var HoverCard = HoverCardPrimitive.Root;
var HoverCardTrigger = HoverCardPrimitive.Trigger;
var HoverCardContent = React21.forwardRef(({ className, align = "center", sideOffset = 4, ...props }, ref) => /* @__PURE__ */ React21.createElement(
  HoverCardPrimitive.Content,
  {
    ref,
    align,
    sideOffset,
    className: cn(
      "z-50 w-64 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-hover-card-content-transform-origin]",
      className
    ),
    ...props
  }
));
HoverCardContent.displayName = HoverCardPrimitive.Content.displayName;

// src/components/input-otp.tsx
import * as React22 from "react";
import { OTPInput, OTPInputContext } from "input-otp";
import { Minus } from "lucide-react";
var InputOTP = React22.forwardRef(({ className, containerClassName, ...props }, ref) => /* @__PURE__ */ React22.createElement(
  OTPInput,
  {
    ref,
    containerClassName: cn(
      "flex items-center gap-2 has-[:disabled]:opacity-50",
      containerClassName
    ),
    className: cn("disabled:cursor-not-allowed", className),
    ...props
  }
));
InputOTP.displayName = "InputOTP";
var InputOTPGroup = React22.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React22.createElement("div", { ref, className: cn("flex items-center", className), ...props }));
InputOTPGroup.displayName = "InputOTPGroup";
var InputOTPSlot = React22.forwardRef(({ index, className, ...props }, ref) => {
  const inputOTPContext = React22.useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext.slots[index] || { char: "", hasFakeCaret: false, isActive: false };
  return /* @__PURE__ */ React22.createElement(
    "div",
    {
      ref,
      className: cn(
        "relative flex h-9 w-9 items-center justify-center border-y border-r border-input text-sm shadow-sm transition-all first:rounded-l-md first:border-l last:rounded-r-md",
        isActive && "z-10 ring-1 ring-ring",
        className
      ),
      ...props
    },
    char,
    hasFakeCaret && /* @__PURE__ */ React22.createElement("div", { className: "pointer-events-none absolute inset-0 flex items-center justify-center" }, /* @__PURE__ */ React22.createElement("div", { className: "h-4 w-px animate-caret-blink bg-foreground duration-1000" }))
  );
});
InputOTPSlot.displayName = "InputOTPSlot";
var InputOTPSeparator = React22.forwardRef(({ ...props }, ref) => /* @__PURE__ */ React22.createElement("div", { ref, role: "separator", ...props }, /* @__PURE__ */ React22.createElement(Minus, null)));
InputOTPSeparator.displayName = "InputOTPSeparator";

// src/components/navigation-menu.tsx
import * as React23 from "react";
import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import { cva as cva5 } from "class-variance-authority";
import { ChevronDown as ChevronDown2 } from "lucide-react";
var NavigationMenu = React23.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ React23.createElement(
  NavigationMenuPrimitive.Root,
  {
    ref,
    className: cn(
      "relative z-10 flex max-w-max flex-1 items-center justify-center",
      className
    ),
    ...props
  },
  children,
  /* @__PURE__ */ React23.createElement(NavigationMenuViewport, null)
));
NavigationMenu.displayName = NavigationMenuPrimitive.Root.displayName;
var NavigationMenuList = React23.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React23.createElement(
  NavigationMenuPrimitive.List,
  {
    ref,
    className: cn(
      "group flex flex-1 list-none items-center justify-center space-x-1",
      className
    ),
    ...props
  }
));
NavigationMenuList.displayName = NavigationMenuPrimitive.List.displayName;
var NavigationMenuItem = NavigationMenuPrimitive.Item;
var navigationMenuTriggerStyle = cva5(
  "group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=open]:text-accent-foreground data-[state=open]:bg-accent/50 data-[state=open]:hover:bg-accent data-[state=open]:focus:bg-accent"
);
var NavigationMenuTrigger = React23.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ React23.createElement(
  NavigationMenuPrimitive.Trigger,
  {
    ref,
    className: cn(navigationMenuTriggerStyle(), "group", className),
    ...props
  },
  children,
  " ",
  /* @__PURE__ */ React23.createElement(
    ChevronDown2,
    {
      className: "relative top-[1px] ml-1 h-3 w-3 transition duration-200 group-data-[state=open]:rotate-180",
      "aria-hidden": "true"
    }
  )
));
NavigationMenuTrigger.displayName = NavigationMenuPrimitive.Trigger.displayName;
var NavigationMenuContent = React23.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React23.createElement(
  NavigationMenuPrimitive.Content,
  {
    ref,
    className: cn(
      "left-0 top-0 w-full data-[motion^=from-]:animate-in data-[motion^=to-]:animate-out data-[motion^=from-]:fade-in data-[motion^=to-]:fade-out data-[motion=from-end]:slide-in-from-right-52 data-[motion=from-start]:slide-in-from-left-52 data-[motion=to-end]:slide-out-to-right-52 data-[motion=to-start]:slide-out-to-left-52 md:absolute md:w-auto ",
      className
    ),
    ...props
  }
));
NavigationMenuContent.displayName = NavigationMenuPrimitive.Content.displayName;
var NavigationMenuLink = NavigationMenuPrimitive.Link;
var NavigationMenuViewport = React23.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React23.createElement("div", { className: cn("absolute left-0 top-full flex justify-center") }, /* @__PURE__ */ React23.createElement(
  NavigationMenuPrimitive.Viewport,
  {
    className: cn(
      "origin-top-center relative mt-1.5 h-[var(--radix-navigation-menu-viewport-height)] w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90 md:w-[var(--radix-navigation-menu-viewport-width)]",
      className
    ),
    ref,
    ...props
  }
)));
NavigationMenuViewport.displayName = NavigationMenuPrimitive.Viewport.displayName;
var NavigationMenuIndicator = React23.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React23.createElement(
  NavigationMenuPrimitive.Indicator,
  {
    ref,
    className: cn(
      "top-full z-[1] flex h-1.5 items-end justify-center overflow-hidden data-[state=visible]:animate-in data-[state=hidden]:animate-out data-[state=hidden]:fade-out data-[state=visible]:fade-in",
      className
    ),
    ...props
  },
  /* @__PURE__ */ React23.createElement("div", { className: "relative top-[60%] h-2 w-2 rotate-45 rounded-tl-sm bg-border shadow-md" })
));
NavigationMenuIndicator.displayName = NavigationMenuPrimitive.Indicator.displayName;

// src/components/pagination.tsx
import * as React24 from "react";
import { ChevronLeft as ChevronLeft3, ChevronRight as ChevronRight5, MoreHorizontal as MoreHorizontal2 } from "lucide-react";
var Pagination = ({ className, ...props }) => /* @__PURE__ */ React24.createElement(
  "nav",
  {
    role: "navigation",
    "aria-label": "pagination",
    className: cn("mx-auto flex w-full justify-center", className),
    ...props
  }
);
Pagination.displayName = "Pagination";
var PaginationContent = React24.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React24.createElement(
  "ul",
  {
    ref,
    className: cn("flex flex-row items-center gap-1", className),
    ...props
  }
));
PaginationContent.displayName = "PaginationContent";
var PaginationItem = React24.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React24.createElement("li", { ref, className: cn("", className), ...props }));
PaginationItem.displayName = "PaginationItem";
var PaginationLink = ({
  className,
  isActive,
  size = "icon",
  ...props
}) => /* @__PURE__ */ React24.createElement(
  "a",
  {
    "aria-current": isActive ? "page" : void 0,
    className: cn(
      buttonVariants({
        variant: isActive ? "outline" : "ghost",
        size
      }),
      className
    ),
    ...props
  }
);
PaginationLink.displayName = "PaginationLink";
var PaginationPrevious = ({
  className,
  ...props
}) => /* @__PURE__ */ React24.createElement(
  PaginationLink,
  {
    "aria-label": "Go to previous page",
    size: "default",
    className: cn("gap-1 pl-2.5", className),
    ...props
  },
  /* @__PURE__ */ React24.createElement(ChevronLeft3, { className: "h-4 w-4" }),
  /* @__PURE__ */ React24.createElement("span", null, "Previous")
);
PaginationPrevious.displayName = "PaginationPrevious";
var PaginationNext = ({
  className,
  ...props
}) => /* @__PURE__ */ React24.createElement(
  PaginationLink,
  {
    "aria-label": "Go to next page",
    size: "default",
    className: cn("gap-1 pr-2.5", className),
    ...props
  },
  /* @__PURE__ */ React24.createElement("span", null, "Next"),
  /* @__PURE__ */ React24.createElement(ChevronRight5, { className: "h-4 w-4" })
);
PaginationNext.displayName = "PaginationNext";
var PaginationEllipsis = ({
  className,
  ...props
}) => /* @__PURE__ */ React24.createElement(
  "span",
  {
    "aria-hidden": true,
    className: cn("flex h-9 w-9 items-center justify-center", className),
    ...props
  },
  /* @__PURE__ */ React24.createElement(MoreHorizontal2, { className: "h-4 w-4" }),
  /* @__PURE__ */ React24.createElement("span", { className: "sr-only" }, "More pages")
);
PaginationEllipsis.displayName = "PaginationEllipsis";

// src/components/popover.tsx
import * as React25 from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
var Popover = PopoverPrimitive.Root;
var PopoverTrigger = PopoverPrimitive.Trigger;
var PopoverPortal = ({ container, ...props }) => /* @__PURE__ */ React25.createElement(PopoverPrimitive.Portal, { container, ...props });
PopoverPortal.displayName = "PopoverPortal";
var PopoverContent = React25.forwardRef(({ className, align = "center", sideOffset = 4, container, ...props }, ref) => /* @__PURE__ */ React25.createElement(PopoverPortal, { container }, /* @__PURE__ */ React25.createElement(
  PopoverPrimitive.Content,
  {
    ref,
    align,
    sideOffset,
    className: cn(
      "z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-popover-content-transform-origin]",
      className
    ),
    ...props
  }
)));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

// src/components/progress.tsx
import * as React26 from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
var Progress = React26.forwardRef(({ className, value, ...props }, ref) => /* @__PURE__ */ React26.createElement(
  ProgressPrimitive.Root,
  {
    ref,
    className: cn(
      "relative h-4 w-full overflow-hidden rounded-full bg-secondary",
      className
    ),
    ...props
  },
  /* @__PURE__ */ React26.createElement(
    ProgressPrimitive.Indicator,
    {
      className: "h-full w-full flex-1 bg-primary transition-all",
      style: { transform: `translateX(-${100 - (value || 0)}%)` }
    }
  )
));
Progress.displayName = ProgressPrimitive.Root.displayName;

// src/components/radio-group.tsx
import * as React27 from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
var RadioGroup2 = React27.forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ React27.createElement(
    RadioGroupPrimitive.Root,
    {
      className: cn("grid gap-2", className),
      ...props,
      ref
    }
  );
});
RadioGroup2.displayName = RadioGroupPrimitive.Root.displayName;
var RadioGroupItem = React27.forwardRef(({ className, iconPosition = "left", iconClassName, isBranded = false, ...props }, ref) => {
  return /* @__PURE__ */ React27.createElement(
    RadioGroupPrimitive.Item,
    {
      ref,
      className: cn(
        "relative flex h-4 w-4 items-center justify-center rounded-full border border-border text-primary ring-offset-background focus:outline-none focus-visible:ring-1 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        isBranded ? "data-[state=checked]:border-[5px] data-[state=checked]:border-brand-primary focus-visible:ring-brand-primary" : "data-[state=checked]:border-[5px] data-[state=checked]:border-primary focus-visible:ring-primary",
        className
      ),
      ...props
    },
    /* @__PURE__ */ React27.createElement(RadioGroupPrimitive.Indicator, { className: "hidden" })
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

// src/components/searchable-select.tsx
import * as React28 from "react";
import { Check as Check3, ChevronsUpDown, Search as Search2 } from "lucide-react";
function SearchableSelect({
  items,
  value,
  onSelect,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No results found",
  maxHeight = "300px",
  className,
  renderItem
}) {
  const [open, setOpen] = React28.useState(false);
  const [search, setSearch] = React28.useState("");
  const inputRef = React28.useRef(null);
  React28.useEffect(() => {
    if (open) {
      setSearch("");
      const timer = setTimeout(() => {
        var _a;
        return (_a = inputRef.current) == null ? void 0 : _a.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [open]);
  const selectedItem = React28.useMemo(
    () => items.find((item) => item.value === value),
    [items, value]
  );
  const filtered = React28.useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(
      (item) => {
        var _a;
        return item.label.toLowerCase().includes(q) || ((_a = item.description) == null ? void 0 : _a.toLowerCase().includes(q));
      }
    );
  }, [items, search]);
  const handleSelect = React28.useCallback(
    (itemValue) => {
      onSelect(itemValue);
      setOpen(false);
    },
    [onSelect]
  );
  return /* @__PURE__ */ React28.createElement(Popover, { open, onOpenChange: setOpen }, /* @__PURE__ */ React28.createElement(PopoverTrigger, { asChild: true }, /* @__PURE__ */ React28.createElement(
    Button,
    {
      variant: "outline",
      role: "combobox",
      "aria-expanded": open,
      className: cn(
        "w-full justify-between font-normal",
        !value && "text-muted-foreground",
        className
      )
    },
    /* @__PURE__ */ React28.createElement("span", { className: "truncate" }, selectedItem ? selectedItem.label : placeholder),
    /* @__PURE__ */ React28.createElement(ChevronsUpDown, { className: "ml-2 h-4 w-4 shrink-0 opacity-50" })
  )), /* @__PURE__ */ React28.createElement(
    PopoverContent,
    {
      className: "w-[--radix-popover-trigger-width] p-0",
      align: "start",
      onWheel: (e) => e.stopPropagation(),
      onTouchMove: (e) => e.stopPropagation()
    },
    /* @__PURE__ */ React28.createElement("div", { className: "flex items-center border-b px-3 py-2" }, /* @__PURE__ */ React28.createElement(Search2, { className: "mr-2 h-4 w-4 shrink-0 text-muted-foreground" }), /* @__PURE__ */ React28.createElement(
      "input",
      {
        ref: inputRef,
        value: search,
        onChange: (e) => setSearch(e.target.value),
        placeholder: searchPlaceholder,
        className: "flex h-8 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
      }
    )),
    /* @__PURE__ */ React28.createElement(
      "div",
      {
        className: "overflow-y-auto p-1",
        style: {
          maxHeight: `min(${maxHeight}, var(--radix-popover-content-available-height, 300px))`
        }
      },
      filtered.length === 0 ? /* @__PURE__ */ React28.createElement("div", { className: "px-2 py-6 text-center text-sm text-muted-foreground" }, emptyMessage) : filtered.map((item) => {
        const isSelected = item.value === value;
        if (renderItem) {
          return /* @__PURE__ */ React28.createElement(
            "button",
            {
              key: item.value,
              type: "button",
              onClick: () => handleSelect(item.value),
              className: "w-full text-left"
            },
            renderItem(item, isSelected)
          );
        }
        return /* @__PURE__ */ React28.createElement(
          "button",
          {
            key: item.value,
            type: "button",
            onClick: () => handleSelect(item.value),
            className: cn(
              "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors",
              "hover:bg-accent hover:text-accent-foreground",
              "focus-visible:bg-accent focus-visible:text-accent-foreground",
              isSelected && "bg-accent/50"
            )
          },
          /* @__PURE__ */ React28.createElement(
            Check3,
            {
              className: cn(
                "h-4 w-4 shrink-0",
                isSelected ? "opacity-100" : "opacity-0"
              )
            }
          ),
          /* @__PURE__ */ React28.createElement("div", { className: "min-w-0 flex-1 text-left" }, /* @__PURE__ */ React28.createElement("div", { className: "truncate" }, item.label), item.description && /* @__PURE__ */ React28.createElement("div", { className: "truncate text-xs text-muted-foreground" }, item.description))
        );
      })
    ),
    items.length > 10 && /* @__PURE__ */ React28.createElement("div", { className: "border-t px-3 py-1.5 text-center text-xs text-muted-foreground" }, search ? `${filtered.length} of ${items.length} items` : `${items.length} items`)
  ));
}

// src/components/select.tsx
import * as React29 from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check as Check4, ChevronDown as ChevronDown3, ChevronUp } from "lucide-react";
var Select = SelectPrimitive.Root;
var SelectGroup = SelectPrimitive.Group;
var SelectValue = SelectPrimitive.Value;
var SelectTrigger = React29.forwardRef(({ className, children, isBranded = false, ...props }, ref) => {
  const ringClasses = isBranded ? "focus:ring-brand-primary" : "focus:ring-ring";
  return /* @__PURE__ */ React29.createElement(
    SelectPrimitive.Trigger,
    {
      ref,
      className: cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
        ringClasses,
        className
      ),
      ...props
    },
    children,
    /* @__PURE__ */ React29.createElement(SelectPrimitive.Icon, { asChild: true }, /* @__PURE__ */ React29.createElement(ChevronDown3, { className: "h-4 w-4 opacity-50" }))
  );
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
var SelectScrollUpButton = React29.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React29.createElement(
  SelectPrimitive.ScrollUpButton,
  {
    ref,
    className: cn(
      "flex cursor-default items-center justify-center py-1",
      className
    ),
    ...props
  },
  /* @__PURE__ */ React29.createElement(ChevronUp, { className: "h-4 w-4" })
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;
var SelectScrollDownButton = React29.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React29.createElement(
  SelectPrimitive.ScrollDownButton,
  {
    ref,
    className: cn(
      "flex cursor-default items-center justify-center py-1",
      className
    ),
    ...props
  },
  /* @__PURE__ */ React29.createElement(ChevronDown3, { className: "h-4 w-4" })
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;
var SelectContent = React29.forwardRef(({ className, children, position = "popper", ...props }, ref) => /* @__PURE__ */ React29.createElement(SelectPrimitive.Portal, null, /* @__PURE__ */ React29.createElement(
  SelectPrimitive.Content,
  {
    ref,
    className: cn(
      "relative z-50 max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-select-content-transform-origin]",
      position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
      className
    ),
    position,
    ...props
  },
  /* @__PURE__ */ React29.createElement(SelectScrollUpButton, null),
  /* @__PURE__ */ React29.createElement(
    SelectPrimitive.Viewport,
    {
      className: cn(
        "p-1",
        position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
      )
    },
    children
  ),
  /* @__PURE__ */ React29.createElement(SelectScrollDownButton, null)
)));
SelectContent.displayName = SelectPrimitive.Content.displayName;
var SelectLabel = React29.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React29.createElement(
  SelectPrimitive.Label,
  {
    ref,
    className: cn("py-1.5 pl-8 pr-2 text-sm font-semibold", className),
    ...props
  }
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;
var SelectItem = React29.forwardRef(({ className, children, isBranded = false, ...props }, ref) => {
  const focusClasses = isBranded ? "focus-visible:ring-brand-primary" : "focus:bg-accent focus:text-accent-foreground focus-visible:ring-ring";
  return /* @__PURE__ */ React29.createElement(
    SelectPrimitive.Item,
    {
      ref,
      className: cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        focusClasses,
        className
      ),
      ...props
    },
    /* @__PURE__ */ React29.createElement("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center" }, /* @__PURE__ */ React29.createElement(SelectPrimitive.ItemIndicator, null, /* @__PURE__ */ React29.createElement(Check4, { className: "h-4 w-4" }))),
    /* @__PURE__ */ React29.createElement(SelectPrimitive.ItemText, null, children)
  );
});
SelectItem.displayName = SelectPrimitive.Item.displayName;
var SelectSeparator = React29.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React29.createElement(
  SelectPrimitive.Separator,
  {
    ref,
    className: cn("-mx-1 my-1 h-px bg-muted", className),
    ...props
  }
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

// src/components/select-or-create.tsx
import * as React30 from "react";
import { Plus, ChevronDown as ChevronDown4 } from "lucide-react";
var SelectOrCreateContext = React30.createContext(null);
function useSelectOrCreate() {
  const context = React30.useContext(SelectOrCreateContext);
  if (!context) {
    throw new Error("SelectOrCreate components must be used within SelectOrCreate.Root");
  }
  return context;
}
function SelectOrCreateRoot({
  children,
  defaultMode = "select",
  selectedValue: controlledSelectedValue,
  onSelectedValueChange,
  onModeChange,
  disabled = false,
  className
}) {
  const [mode, setModeInternal] = React30.useState(defaultMode);
  const [internalSelectedValue, setInternalSelectedValue] = React30.useState();
  const selectedValue = controlledSelectedValue ?? internalSelectedValue;
  const setMode = React30.useCallback((newMode) => {
    setModeInternal(newMode);
    onModeChange == null ? void 0 : onModeChange(newMode);
  }, [onModeChange]);
  const setSelectedValue = React30.useCallback((value) => {
    setInternalSelectedValue(value);
    onSelectedValueChange == null ? void 0 : onSelectedValueChange(value);
    if (value) {
      setMode("select");
    }
  }, [onSelectedValueChange, setMode]);
  React30.useEffect(() => {
    setModeInternal(defaultMode);
  }, [defaultMode]);
  return /* @__PURE__ */ React30.createElement(
    SelectOrCreateContext.Provider,
    {
      value: { mode, setMode, selectedValue, setSelectedValue, isDisabled: disabled }
    },
    /* @__PURE__ */ React30.createElement("div", { className: cn("space-y-3", className) }, children)
  );
}
function SelectOrCreateSelect({
  placeholder = "Select an option",
  children,
  className
}) {
  const { selectedValue, setSelectedValue, isDisabled } = useSelectOrCreate();
  return /* @__PURE__ */ React30.createElement(
    Select,
    {
      value: selectedValue ?? "",
      onValueChange: (value) => setSelectedValue(value || void 0),
      disabled: isDisabled
    },
    /* @__PURE__ */ React30.createElement(SelectTrigger, { className }, /* @__PURE__ */ React30.createElement(SelectValue, { placeholder })),
    /* @__PURE__ */ React30.createElement(SelectContent, null, children)
  );
}
function SelectOrCreateButton({ children, className }) {
  const { mode, setMode, setSelectedValue, isDisabled } = useSelectOrCreate();
  const handleClick = () => {
    if (mode === "create") {
      setMode("select");
    } else {
      setSelectedValue(void 0);
      setMode("create");
    }
  };
  return /* @__PURE__ */ React30.createElement(
    Button,
    {
      type: "button",
      variant: "outline",
      size: "sm",
      onClick: handleClick,
      disabled: isDisabled,
      className: cn("w-full justify-start", className)
    },
    /* @__PURE__ */ React30.createElement(Plus, { className: cn(
      "h-4 w-4 mr-2 transition-transform duration-150 ease-out",
      mode === "create" && "rotate-45"
    ) }),
    children,
    /* @__PURE__ */ React30.createElement(ChevronDown4, { className: cn(
      "h-4 w-4 ml-auto transition-transform duration-150 ease-out",
      mode === "create" && "rotate-180"
    ) })
  );
}
function SelectOrCreateForm({ children, className }) {
  const { mode } = useSelectOrCreate();
  const isOpen = mode === "create";
  return /* @__PURE__ */ React30.createElement(
    "div",
    {
      className: cn(
        "grid transition-[grid-template-rows,opacity] duration-150 ease-out",
        isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
      )
    },
    /* @__PURE__ */ React30.createElement("div", { className: "overflow-hidden" }, /* @__PURE__ */ React30.createElement("div", { className: cn("pt-4 space-y-4 border-t border-dashed", className) }, children))
  );
}
function SelectOrCreatePreview({ children, className }) {
  const { mode, selectedValue } = useSelectOrCreate();
  const isVisible = mode !== "create" && !!selectedValue;
  return /* @__PURE__ */ React30.createElement(
    "div",
    {
      className: cn(
        "grid transition-[grid-template-rows,opacity] duration-150 ease-out",
        isVisible ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
      )
    },
    /* @__PURE__ */ React30.createElement("div", { className: "overflow-hidden" }, /* @__PURE__ */ React30.createElement("div", { className: cn("rounded-md border p-3 bg-muted/50", className) }, children))
  );
}
var SelectOrCreate = {
  Root: SelectOrCreateRoot,
  Select: SelectOrCreateSelect,
  Button: SelectOrCreateButton,
  Form: SelectOrCreateForm,
  Preview: SelectOrCreatePreview
};

// src/components/separator.tsx
import * as React31 from "react";
import * as SeparatorPrimitive from "@radix-ui/react-separator";
var Separator3 = React31.forwardRef(
  ({ className, orientation = "horizontal", decorative = true, ...props }, ref) => /* @__PURE__ */ React31.createElement(
    SeparatorPrimitive.Root,
    {
      ref,
      decorative,
      orientation,
      className: cn(
        "shrink-0 bg-border",
        orientation === "horizontal" ? "h-[1px] w-full" : "h-full w-[1px]",
        className
      ),
      ...props
    }
  )
);
Separator3.displayName = SeparatorPrimitive.Root.displayName;

// src/components/sheet.tsx
import * as React32 from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva as cva6 } from "class-variance-authority";
import { X as X2 } from "lucide-react";
var Sheet = SheetPrimitive.Root;
var SheetTrigger = SheetPrimitive.Trigger;
var SheetClose = SheetPrimitive.Close;
var SheetPortal = ({ container, ...props }) => /* @__PURE__ */ React32.createElement(
  SheetPrimitive.Portal,
  {
    container,
    ...props
  }
);
SheetPortal.displayName = "SheetPortal";
var SheetOverlay = React32.forwardRef(({ className, isContained = false, ...props }, ref) => /* @__PURE__ */ React32.createElement(
  SheetPrimitive.Overlay,
  {
    className: cn(
      // Use absolute positioning when contained in Shadow DOM, fixed when not
      // z-[60] ensures overlay appears above sticky headers (z-50)
      isContained ? "absolute inset-0 z-60 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 sheet-overlay" : "fixed inset-0 z-60 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props,
    ref
  }
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;
var sheetVariants = cva6(
  "z-60 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom: "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right: "inset-y-0 right-0 h-full w-3/4  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm"
      }
    },
    defaultVariants: {
      side: "right"
    }
  }
);
var SheetContent = React32.forwardRef(({ side = "right", className, children, container, ...props }, ref) => {
  const isContained = !!container;
  return /* @__PURE__ */ React32.createElement(SheetPortal, { container }, /* @__PURE__ */ React32.createElement(SheetOverlay, { isContained }), /* @__PURE__ */ React32.createElement(
    SheetPrimitive.Content,
    {
      ref,
      className: cn(
        // Use absolute positioning when contained in Shadow DOM
        isContained ? "absolute" : "fixed",
        sheetVariants({ side }),
        className
      ),
      ...props
    },
    children,
    /* @__PURE__ */ React32.createElement(SheetPrimitive.Close, { className: "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary" }, /* @__PURE__ */ React32.createElement(X2, { className: "h-4 w-4" }), /* @__PURE__ */ React32.createElement("span", { className: "sr-only" }, "Close"))
  ));
});
SheetContent.displayName = SheetPrimitive.Content.displayName;
var SheetHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ React32.createElement(
  "div",
  {
    className: cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    ),
    ...props
  }
);
SheetHeader.displayName = "SheetHeader";
var SheetFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ React32.createElement(
  "div",
  {
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
SheetFooter.displayName = "SheetFooter";
var SheetTitle = React32.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React32.createElement(
  SheetPrimitive.Title,
  {
    ref,
    className: cn("text-lg font-semibold text-foreground", className),
    ...props
  }
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;
var SheetDescription = React32.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React32.createElement(
  SheetPrimitive.Description,
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

// src/components/skeleton.tsx
import React33 from "react";
function Skeleton({
  className,
  ...props
}) {
  return /* @__PURE__ */ React33.createElement(
    "div",
    {
      className: cn("animate-pulse rounded-md bg-muted", className),
      ...props
    }
  );
}

// src/components/slider.tsx
import * as React34 from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
var Slider = React34.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React34.createElement(
  SliderPrimitive.Root,
  {
    ref,
    className: cn(
      "relative flex w-full touch-none select-none items-center",
      className
    ),
    ...props
  },
  /* @__PURE__ */ React34.createElement(SliderPrimitive.Track, { className: "relative h-2 w-full grow overflow-hidden rounded-full bg-secondary" }, /* @__PURE__ */ React34.createElement(SliderPrimitive.Range, { className: "absolute h-full bg-primary" })),
  /* @__PURE__ */ React34.createElement(SliderPrimitive.Thumb, { className: "block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50" })
));
Slider.displayName = SliderPrimitive.Root.displayName;

// src/components/switch.tsx
import * as React35 from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";
var Switch = React35.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React35.createElement(
  SwitchPrimitives.Root,
  {
    className: cn(
      "peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
      className
    ),
    ...props,
    ref
  },
  /* @__PURE__ */ React35.createElement(
    SwitchPrimitives.Thumb,
    {
      className: cn(
        "pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0"
      )
    }
  )
));
Switch.displayName = SwitchPrimitives.Root.displayName;

// src/components/table.tsx
import * as React36 from "react";
var Table = React36.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React36.createElement("div", { className: "relative w-full overflow-auto" }, /* @__PURE__ */ React36.createElement(
  "table",
  {
    ref,
    className: cn("w-full caption-bottom text-sm", className),
    ...props
  }
)));
Table.displayName = "Table";
var TableHeader = React36.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React36.createElement("thead", { ref, className: cn("[&_tr]:border-b", className), ...props }));
TableHeader.displayName = "TableHeader";
var TableBody = React36.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React36.createElement(
  "tbody",
  {
    ref,
    className: cn("[&_tr:last-child]:border-0", className),
    ...props
  }
));
TableBody.displayName = "TableBody";
var TableFooter = React36.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React36.createElement(
  "tfoot",
  {
    ref,
    className: cn(
      "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
      className
    ),
    ...props
  }
));
TableFooter.displayName = "TableFooter";
var TableRow = React36.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React36.createElement(
  "tr",
  {
    ref,
    className: cn(
      "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
      className
    ),
    ...props
  }
));
TableRow.displayName = "TableRow";
var TableHead = React36.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React36.createElement(
  "th",
  {
    ref,
    className: cn(
      "h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
      className
    ),
    ...props
  }
));
TableHead.displayName = "TableHead";
var TableCell = React36.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React36.createElement(
  "td",
  {
    ref,
    className: cn("p-4 align-middle [&:has([role=checkbox])]:pr-0", className),
    ...props
  }
));
TableCell.displayName = "TableCell";
var TableCaption = React36.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React36.createElement(
  "caption",
  {
    ref,
    className: cn("mt-4 text-sm text-muted-foreground", className),
    ...props
  }
));
TableCaption.displayName = "TableCaption";

// src/components/tabs.tsx
import * as React37 from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
var Tabs = TabsPrimitive.Root;
var TabsList = React37.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React37.createElement(
  TabsPrimitive.List,
  {
    ref,
    className: cn(
      "inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground",
      className
    ),
    ...props
  }
));
TabsList.displayName = TabsPrimitive.List.displayName;
var TabsTrigger = React37.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React37.createElement(
  TabsPrimitive.Trigger,
  {
    ref,
    className: cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm",
      className
    ),
    ...props
  }
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;
var TabsContent = React37.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React37.createElement(
  TabsPrimitive.Content,
  {
    ref,
    className: cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    ),
    ...props
  }
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

// src/components/textarea.tsx
import * as React38 from "react";
var Textarea = React38.forwardRef(({ className, isBranded = false, ...props }, ref) => {
  const brandedClasses = isBranded ? "focus-visible:ring-brand-primary" : "focus-visible:ring-ring";
  return /* @__PURE__ */ React38.createElement(
    "textarea",
    {
      className: cn(
        "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        brandedClasses,
        className
      ),
      ref,
      ...props
    }
  );
});
Textarea.displayName = "Textarea";

// src/components/toggle.tsx
import * as React39 from "react";
import * as TogglePrimitive from "@radix-ui/react-toggle";
import { cva as cva7 } from "class-variance-authority";
var toggleVariants = cva7(
  "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 gap-2",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline: "border border-input bg-transparent hover:bg-accent hover:text-accent-foreground"
      },
      size: {
        default: "h-10 px-3 min-w-10",
        sm: "h-9 px-2.5 min-w-9",
        lg: "h-11 px-5 min-w-11"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Toggle = React39.forwardRef(({ className, variant, size, ...props }, ref) => /* @__PURE__ */ React39.createElement(
  TogglePrimitive.Root,
  {
    ref,
    className: cn(toggleVariants({ variant, size, className })),
    ...props
  }
));
Toggle.displayName = TogglePrimitive.Root.displayName;

// src/components/tooltip.tsx
import * as React40 from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
var TooltipProvider = TooltipPrimitive.Provider;
var Tooltip = TooltipPrimitive.Root;
var TooltipTrigger = TooltipPrimitive.Trigger;
var TooltipContent = React40.forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ React40.createElement(
  TooltipPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]",
      className
    ),
    ...props
  }
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// src/components/index.ts
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

// src/components/domain/quantity-counter.tsx
import * as React41 from "react";
import { Minus as Minus2, Plus as Plus2 } from "lucide-react";
var QuantityCounter = React41.memo(({
  value,
  onChange,
  min = 1,
  max = 99,
  disabled = false,
  className
}) => {
  const increment = React41.useCallback(() => {
    if (disabled || value >= max) return;
    onChange(value + 1);
  }, [value, max, onChange, disabled]);
  const decrement = React41.useCallback(() => {
    if (disabled || value <= min) return;
    onChange(value - 1);
  }, [value, min, onChange, disabled]);
  const isDecrementDisabled = disabled || value <= min;
  const isIncrementDisabled = disabled || value >= max;
  return /* @__PURE__ */ React41.createElement("div", { className: cn("quantity-counter", className) }, /* @__PURE__ */ React41.createElement(
    "button",
    {
      type: "button",
      onClick: decrement,
      disabled: isDecrementDisabled,
      className: isDecrementDisabled ? "opacity-50 cursor-not-allowed" : "",
      "aria-label": "Decrease quantity"
    },
    /* @__PURE__ */ React41.createElement(Minus2, { className: "h-3.5 w-3.5" })
  ), /* @__PURE__ */ React41.createElement("span", null, value), /* @__PURE__ */ React41.createElement(
    "button",
    {
      type: "button",
      onClick: increment,
      disabled: isIncrementDisabled,
      className: isIncrementDisabled ? "opacity-50 cursor-not-allowed" : "",
      "aria-label": "Increase quantity"
    },
    /* @__PURE__ */ React41.createElement(Plus2, { className: "h-3.5 w-3.5" })
  ));
});
QuantityCounter.displayName = "QuantityCounter";

// src/components/domain/smart-price.tsx
import * as React42 from "react";
import { Star, TrendingDown } from "lucide-react";
import { getEffectivePrice } from "@flote/pricing";
var sizeStyles = {
  sm: {
    price: "text-sm font-medium",
    originalPrice: "text-xs",
    badge: "text-[10px] px-1 py-0.5",
    icon: "h-2.5 w-2.5"
  },
  md: {
    price: "text-base font-medium",
    originalPrice: "text-sm",
    badge: "text-[11px] px-1.5 py-0.5",
    icon: "h-3 w-3"
  },
  lg: {
    price: "text-2xl font-medium",
    originalPrice: "text-lg",
    badge: "text-xs px-1.5 py-0.5",
    icon: "h-3 w-3"
  }
};
var SmartPrice = ({
  product,
  quantity = 1,
  showLabel = true,
  size = "md",
  className = "",
  variant = "detailed"
}) => {
  const priceInfo = React42.useMemo(() => {
    var _a;
    const customerPricing = product.customerPrice;
    const standardPrice = ((_a = product.pricing) == null ? void 0 : _a.price) ? parseFloat(product.pricing.price) : null;
    if (!standardPrice) {
      return {
        price: 0,
        formattedPrice: "N/A",
        priceType: "standard"
      };
    }
    const customerPrice = customerPricing ? parseFloat(customerPricing.price) : void 0;
    const { effectivePrice, priceType } = getEffectivePrice({
      standardPrice,
      customerPrice,
      quantity,
      discountTiers: product.discountTiers
    });
    let savings = 0;
    let savingsPercentage = 0;
    if (priceType !== "standard" && standardPrice > effectivePrice) {
      savings = standardPrice - effectivePrice;
      savingsPercentage = Math.round(savings / standardPrice * 100);
    }
    return {
      price: effectivePrice,
      formattedPrice: `$${effectivePrice.toFixed(2)}`,
      priceType,
      customerPricing: priceType === "customer" ? customerPricing : void 0,
      originalPrice: priceType !== "standard" ? standardPrice : void 0,
      savings: savings > 0 ? savings : void 0,
      savingsPercentage: savingsPercentage > 0 ? savingsPercentage : void 0
    };
  }, [product, quantity]);
  const styles = sizeStyles[size];
  if (priceInfo.formattedPrice === "N/A") {
    return /* @__PURE__ */ React42.createElement("div", { className: cn("flex items-center gap-2", className) }, /* @__PURE__ */ React42.createElement("span", { className: cn("text-muted-foreground", styles.price) }, "Log in to see price"));
  }
  const hasDiscount = priceInfo.priceType !== "standard";
  const isCustomerPrice = priceInfo.priceType === "customer";
  const isBulkPrice = priceInfo.priceType === "bulk";
  if (variant === "compact") {
    if (hasDiscount && priceInfo.originalPrice) {
      return /* @__PURE__ */ React42.createElement("div", { className: cn("flex items-center gap-2", className) }, /* @__PURE__ */ React42.createElement("span", { className: cn("line-through text-muted-foreground", styles.originalPrice) }, "$", priceInfo.originalPrice.toFixed(2)), /* @__PURE__ */ React42.createElement("span", { className: cn(styles.price, "text-green-600 font-semibold") }, priceInfo.formattedPrice));
    }
    return /* @__PURE__ */ React42.createElement("div", { className: cn("flex items-center gap-2", className) }, /* @__PURE__ */ React42.createElement("span", { className: cn(styles.price, "text-foreground") }, priceInfo.formattedPrice));
  }
  return /* @__PURE__ */ React42.createElement("div", { className: cn("flex flex-col gap-1", className) }, /* @__PURE__ */ React42.createElement("div", { className: "flex items-center gap-2 flex-wrap" }, /* @__PURE__ */ React42.createElement("span", { className: cn(styles.price, hasDiscount ? "text-green-600" : "text-foreground") }, priceInfo.formattedPrice), isCustomerPrice && showLabel && /* @__PURE__ */ React42.createElement(
    Badge,
    {
      variant: "secondary",
      className: cn(styles.badge, "bg-green-50 text-green-700 border-green-200 hover:bg-green-100 flex items-center gap-0.5")
    },
    /* @__PURE__ */ React42.createElement(Star, { className: cn(styles.icon, "fill-current") }),
    /* @__PURE__ */ React42.createElement("span", null, "Your Price")
  ), isBulkPrice && showLabel && /* @__PURE__ */ React42.createElement(
    Badge,
    {
      variant: "secondary",
      className: cn(styles.badge, "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 flex items-center gap-0.5")
    },
    /* @__PURE__ */ React42.createElement(TrendingDown, { className: styles.icon }),
    /* @__PURE__ */ React42.createElement("span", null, "Bulk")
  ), priceInfo.savingsPercentage && priceInfo.savingsPercentage > 0 && /* @__PURE__ */ React42.createElement(
    Badge,
    {
      variant: "secondary",
      className: cn(styles.badge, "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 flex items-center gap-1")
    },
    /* @__PURE__ */ React42.createElement("span", null, "Save ", priceInfo.savingsPercentage, "%")
  )), priceInfo.originalPrice && priceInfo.savings && /* @__PURE__ */ React42.createElement("div", { className: "flex items-center gap-2" }, /* @__PURE__ */ React42.createElement("span", { className: cn("line-through text-muted-foreground", styles.originalPrice) }, "$", priceInfo.originalPrice.toFixed(2)), /* @__PURE__ */ React42.createElement("span", { className: cn("text-green-600 font-medium", styles.originalPrice) }, "Save $", priceInfo.savings.toFixed(2))));
};

// src/components/domain/stock-badge.tsx
import * as React43 from "react";
import { AlertTriangle } from "lucide-react";
import { B2BWaveProductStatuses } from "@flote/constants";
var statusConfig = {
  in_stock: null,
  // No badge for in-stock items
  out_of_stock: {
    label: "Out of Stock",
    className: "bg-red-50 text-red-800 border border-red-200 hover:bg-red-100",
    showIcon: true
  },
  backordered: {
    label: "Backordered",
    className: "bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100",
    showIcon: true
  }
};
var StockBadge = ({ status, className }) => {
  const config = statusConfig[status];
  if (!config) {
    return null;
  }
  return /* @__PURE__ */ React43.createElement(Badge, { className: cn(config.className, "flex items-center gap-1", className) }, config.showIcon && /* @__PURE__ */ React43.createElement(AlertTriangle, { className: "h-3 w-3" }), /* @__PURE__ */ React43.createElement("span", null, config.label));
};
function getStockStatus(totalInventory, canBackorder, statusId) {
  if (statusId === B2BWaveProductStatuses.BACKORDER) {
    return "backordered";
  }
  if (totalInventory > 0) {
    return "in_stock";
  }
  if (canBackorder) {
    return "backordered";
  }
  return "out_of_stock";
}

// src/components/domain/product-card.tsx
import * as React44 from "react";
import { getTotalInventory } from "@flote/utils";
var defaultImageSizes = "(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw";
var srcSetWidths = [320, 480, 640, 800, 1024];
var ProductCard = React44.memo(({
  product,
  onProductClick,
  isPriority = false,
  className,
  transformImageUrl,
  fallbackImageUrl
}) => {
  const [imageError, setImageError] = React44.useState(false);
  const handleClick = React44.useCallback(() => {
    onProductClick(product);
  }, [product, onProductClick]);
  const handleImageError = React44.useCallback(() => {
    if (fallbackImageUrl && !imageError) {
      setImageError(true);
    }
  }, [fallbackImageUrl, imageError]);
  React44.useEffect(() => {
    setImageError(false);
  }, [product.id]);
  const effectiveImageUrl = imageError ? fallbackImageUrl : product.imageUrl;
  const imageSrc = React44.useMemo(() => {
    if (!transformImageUrl) return effectiveImageUrl || "";
    return transformImageUrl(effectiveImageUrl, {
      width: 400,
      height: 400,
      crop: "fill",
      quality: "auto",
      format: "auto"
    });
  }, [effectiveImageUrl, transformImageUrl]);
  const imageSrcSet = React44.useMemo(() => {
    if (!effectiveImageUrl || !transformImageUrl) return void 0;
    return srcSetWidths.map((width) => {
      const url = transformImageUrl(effectiveImageUrl, {
        width,
        height: width,
        crop: "fill",
        quality: "auto",
        format: "auto"
      });
      return `${url} ${width}w`;
    }).join(", ");
  }, [effectiveImageUrl, transformImageUrl]);
  const totalInventory = React44.useMemo(() => getTotalInventory(product), [product]);
  const stockStatus = React44.useMemo(
    () => getStockStatus(totalInventory, product.canBackorder ?? void 0, product.statusId ?? void 0),
    [totalInventory, product.canBackorder, product.statusId]
  );
  return /* @__PURE__ */ React44.createElement(
    "div",
    {
      className: cn("product-card group cursor-pointer", className),
      onClick: handleClick,
      "aria-label": `View ${product.name} details`
    },
    /* @__PURE__ */ React44.createElement("div", { className: "product-image-container aspect-square" }, /* @__PURE__ */ React44.createElement(
      "img",
      {
        src: imageSrc,
        srcSet: imageSrcSet,
        sizes: defaultImageSizes,
        alt: product.name,
        className: "product-image animate-blur-in h-full w-full object-cover",
        loading: isPriority ? "eager" : "lazy",
        fetchPriority: isPriority ? "high" : "auto",
        onError: handleImageError
      }
    ), /* @__PURE__ */ React44.createElement("div", { className: "absolute top-2 right-2 flex flex-col gap-1 items-end" }, product.promoteFront && /* @__PURE__ */ React44.createElement("span", { className: "inline-flex items-center rounded-full bg-black px-2.5 py-0.5 text-xs font-medium text-white" }, "Featured"), /* @__PURE__ */ React44.createElement(StockBadge, { status: stockStatus }))),
    /* @__PURE__ */ React44.createElement("div", { className: "space-y-1 pt-2" }, /* @__PURE__ */ React44.createElement("h3", { className: "font-medium text-base leading-tight text-foreground group-hover:text-primary transition-colors" }, product.name), /* @__PURE__ */ React44.createElement("div", { className: "flex justify-between items-center" }, /* @__PURE__ */ React44.createElement("p", { className: "text-sm text-muted-foreground" }, product == null ? void 0 : product.categoryPath), /* @__PURE__ */ React44.createElement(
      SmartPrice,
      {
        product,
        size: "sm",
        showLabel: false,
        variant: "compact",
        className: "text-right"
      }
    )))
  );
});
ProductCard.displayName = "ProductCard";

// src/components/domain/brand-card.tsx
import * as React45 from "react";
var BrandCard = React45.memo(({
  brand,
  className,
  renderAction,
  onViewProducts
}) => {
  const handleClick = React45.useCallback(() => {
    onViewProducts == null ? void 0 : onViewProducts(brand);
  }, [brand, onViewProducts]);
  const logoUrl = (brand == null ? void 0 : brand.imageUrl) || (brand == null ? void 0 : brand.logoUrl) || null;
  return /* @__PURE__ */ React45.createElement(Card, { className: cn("overflow-hidden flex flex-col", className) }, /* @__PURE__ */ React45.createElement("div", { className: "aspect-[4/3] sm:aspect-square relative bg-secondary/30" }, /* @__PURE__ */ React45.createElement("div", { className: "absolute inset-4 sm:inset-6 flex items-center justify-center" }, logoUrl ? /* @__PURE__ */ React45.createElement(
    "img",
    {
      src: logoUrl,
      alt: brand.name,
      className: "max-w-full max-h-full object-contain"
    }
  ) : /* @__PURE__ */ React45.createElement("div", { className: "text-muted-foreground text-lg font-medium" }, brand.name))), /* @__PURE__ */ React45.createElement(CardContent, { className: "p-4 sm:p-6 flex-1" }, /* @__PURE__ */ React45.createElement("h2", { className: "text-xl font-medium" }, brand.name)), /* @__PURE__ */ React45.createElement(CardFooter, { className: "p-4 sm:p-6 pt-0" }, renderAction ? renderAction(brand) : /* @__PURE__ */ React45.createElement(Button, { className: "w-full", onClick: handleClick }, "View Products")));
});
BrandCard.displayName = "BrandCard";

// src/components/domain/product-grid.tsx
import * as React46 from "react";
var ProductGrid = React46.memo(({
  products,
  onProductClick,
  isLoading = false,
  className,
  transformImageUrl,
  onResetFilters,
  skeletonCount = 8,
  priorityCount = 9,
  emptyMessage = "No products found matching your criteria.",
  fallbackImageUrl
}) => {
  if (isLoading) {
    return /* @__PURE__ */ React46.createElement("div", { className: cn("grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4", className) }, [...Array(skeletonCount)].map((_, i) => /* @__PURE__ */ React46.createElement("div", { key: i, className: "animate-pulse" }, /* @__PURE__ */ React46.createElement("div", { className: "aspect-square bg-muted rounded-md mb-2" }), /* @__PURE__ */ React46.createElement("div", { className: "h-4 bg-muted rounded w-3/4 mb-2" }), /* @__PURE__ */ React46.createElement("div", { className: "h-4 bg-muted rounded w-1/4" }))));
  }
  if (products.length === 0) {
    return /* @__PURE__ */ React46.createElement("div", { className: "rounded-lg border border-dashed p-8 text-center" }, /* @__PURE__ */ React46.createElement("p", { className: "text-muted-foreground" }, emptyMessage), onResetFilters && /* @__PURE__ */ React46.createElement(
      "button",
      {
        onClick: onResetFilters,
        className: "mt-4 text-sm text-primary hover:underline"
      },
      "Reset filters"
    ));
  }
  return /* @__PURE__ */ React46.createElement("div", { className: cn("grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4", className) }, products.map((product, index) => /* @__PURE__ */ React46.createElement(
    ProductCard,
    {
      key: product.id,
      product,
      onProductClick,
      isPriority: index < priorityCount,
      transformImageUrl,
      fallbackImageUrl
    }
  )));
});
ProductGrid.displayName = "ProductGrid";

// src/components/map/RouteMap.tsx
import React47, { useRef as useRef2, useCallback as useCallback7, useEffect as useEffect4, useState as useState5 } from "react";
import Map, { Source, Layer, Marker, NavigationControl } from "react-map-gl/mapbox";
import { CircleDot, Flag, Navigation2 } from "lucide-react";
import "mapbox-gl/dist/mapbox-gl.css";
var ROUTE_COLORS = [
  "#3B82F6",
  // blue-500
  "#EF4444",
  // red-500
  "#22C55E",
  // green-500
  "#A855F7",
  // purple-500
  "#F97316",
  // orange-500
  "#06B6D4",
  // cyan-500
  "#EC4899",
  // pink-500
  "#84CC16"
  // lime-500
];
function routeToGeoJson(route, color) {
  const coordinates = route.routePoints.map((p) => [p.lng, p.lat]);
  return {
    type: "Feature",
    properties: {
      taskId: route.taskId,
      color,
      trackingId: route.trackingId
    },
    geometry: {
      type: "LineString",
      coordinates
    }
  };
}
function RouteMap({
  mapboxToken,
  data,
  selectedRouteId,
  onRouteSelect,
  isLoading,
  className = "",
  hiddenRouteIds = /* @__PURE__ */ new Set(),
  initialCenter = { lat: 46.5, lng: -112.5 },
  initialZoom = 6
}) {
  const mapRef = useRef2(null);
  const [mapLoaded, setMapLoaded] = useState5(false);
  useEffect4(() => {
    if (!mapRef.current || !(data == null ? void 0 : data.bounds) || !mapLoaded) return;
    mapRef.current.fitBounds(
      [
        [data.bounds.sw.lng, data.bounds.sw.lat],
        [data.bounds.ne.lng, data.bounds.ne.lat]
      ],
      { padding: 60, duration: 500 }
    );
  }, [data == null ? void 0 : data.bounds, mapLoaded]);
  const handleMapLoad = useCallback7(() => {
    setMapLoaded(true);
  }, []);
  const handleMapClick = useCallback7(
    (event) => {
      var _a;
      if (!onRouteSelect) return;
      const features = event.features;
      if (features && features.length > 0) {
        const firstFeature = features[0];
        const taskId = (_a = firstFeature == null ? void 0 : firstFeature.properties) == null ? void 0 : _a.taskId;
        if (taskId) {
          onRouteSelect(taskId === selectedRouteId ? null : taskId);
          return;
        }
      }
      onRouteSelect(null);
    },
    [onRouteSelect, selectedRouteId]
  );
  if (!mapboxToken) {
    return /* @__PURE__ */ React47.createElement("div", { className: `flex items-center justify-center bg-muted rounded-lg ${className}` }, /* @__PURE__ */ React47.createElement("p", { className: "text-muted-foreground text-sm" }, "Mapbox token not configured"));
  }
  const allRoutes = (data == null ? void 0 : data.routes) ?? [];
  const routes = allRoutes.filter((route) => !hiddenRouteIds.has(route.taskId));
  const routeColorMap = {};
  allRoutes.forEach((route, idx) => {
    routeColorMap[route.taskId] = ROUTE_COLORS[idx % ROUTE_COLORS.length];
  });
  const routeFeatures = routes.map((route) => {
    const color = routeColorMap[route.taskId] ?? ROUTE_COLORS[0];
    return routeToGeoJson(route, color);
  });
  const geojsonData = {
    type: "FeatureCollection",
    features: routeFeatures
  };
  const routeLineLayer = {
    id: "route-lines",
    type: "line",
    paint: {
      "line-color": ["get", "color"],
      "line-width": ["case", ["==", ["get", "taskId"], selectedRouteId || ""], 5, 3],
      "line-opacity": ["case", ["==", ["get", "taskId"], selectedRouteId || ""], 1, 0.7]
    },
    layout: {
      "line-cap": "round",
      "line-join": "round"
    }
  };
  const routeClickLayer = {
    id: "route-click-target",
    type: "circle",
    paint: {
      "circle-radius": 8,
      "circle-opacity": 0
    }
  };
  const initialViewState = {
    latitude: initialCenter.lat,
    longitude: initialCenter.lng,
    zoom: initialZoom
  };
  return /* @__PURE__ */ React47.createElement("div", { className: `relative ${className}` }, isLoading && /* @__PURE__ */ React47.createElement("div", { className: "absolute inset-0 z-10 flex items-center justify-center bg-background/50" }, /* @__PURE__ */ React47.createElement("div", { className: "h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" })), /* @__PURE__ */ React47.createElement(
    Map,
    {
      ref: mapRef,
      mapboxAccessToken: mapboxToken,
      initialViewState,
      style: { width: "100%", height: "100%" },
      mapStyle: "mapbox://styles/mapbox/streets-v12",
      onLoad: handleMapLoad,
      onClick: handleMapClick,
      interactiveLayerIds: ["route-lines"],
      cursor: "pointer"
    },
    /* @__PURE__ */ React47.createElement(NavigationControl, { position: "top-right" }),
    routeFeatures.length > 0 && /* @__PURE__ */ React47.createElement(Source, { id: "routes", type: "geojson", data: geojsonData }, /* @__PURE__ */ React47.createElement(Layer, { ...routeLineLayer }), /* @__PURE__ */ React47.createElement(Layer, { ...routeClickLayer })),
    routes.map((route) => {
      const startPoint = route.startPoint ?? route.routePoints[0];
      if (!startPoint) return null;
      const color = routeColorMap[route.taskId] ?? ROUTE_COLORS[0];
      const isSelected = route.taskId === selectedRouteId;
      return /* @__PURE__ */ React47.createElement(
        Marker,
        {
          key: `start-${route.taskId}`,
          latitude: startPoint.lat,
          longitude: startPoint.lng,
          anchor: "center"
        },
        /* @__PURE__ */ React47.createElement(
          CircleDot,
          {
            className: "h-6 w-6 drop-shadow-md",
            style: {
              color,
              filter: isSelected ? `drop-shadow(0 0 3px ${color})` : void 0
            },
            strokeWidth: isSelected ? 3 : 2.5
          }
        )
      );
    }),
    routes.map((route) => {
      const color = routeColorMap[route.taskId] ?? ROUTE_COLORS[0];
      const isSelected = route.taskId === selectedRouteId;
      return /* @__PURE__ */ React47.createElement(
        Marker,
        {
          key: `end-${route.taskId}`,
          latitude: route.endPoint.lat,
          longitude: route.endPoint.lng,
          anchor: "bottom-left",
          onClick: () => onRouteSelect == null ? void 0 : onRouteSelect(route.taskId)
        },
        /* @__PURE__ */ React47.createElement(
          "div",
          {
            className: `flex flex-col items-center cursor-pointer transition-transform ${isSelected ? "scale-125" : "hover:scale-110"}`
          },
          /* @__PURE__ */ React47.createElement(
            Flag,
            {
              className: "h-7 w-7 drop-shadow-md",
              style: { color, fill: color, fillOpacity: 0.3 }
            }
          )
        )
      );
    }),
    routes.map((route) => {
      if (route.status === "completed" || route.routePoints.length === 0) return null;
      const lastPoint = route.routePoints[route.routePoints.length - 1];
      if (!lastPoint) return null;
      const color = routeColorMap[route.taskId] ?? ROUTE_COLORS[0];
      const isSelected = route.taskId === selectedRouteId;
      return /* @__PURE__ */ React47.createElement(
        Marker,
        {
          key: `driver-${route.taskId}`,
          latitude: lastPoint.lat,
          longitude: lastPoint.lng,
          anchor: "center"
        },
        /* @__PURE__ */ React47.createElement(
          "div",
          {
            className: `p-1.5 rounded-full border-2 border-white shadow-lg animate-pulse ${isSelected ? "ring-2 ring-primary" : ""}`,
            style: { backgroundColor: color }
          },
          /* @__PURE__ */ React47.createElement(Navigation2, { className: "h-4 w-4 text-white" })
        )
      );
    })
  ));
}

// src/components/map/RouteLegend.tsx
import React48 from "react";
import { Clock, CheckCircle, XCircle, User, MapPin, Eye, EyeOff } from "lucide-react";
function formatTime(isoDate) {
  if (!isoDate) return "";
  return new Date(isoDate).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
function formatFailureReason(reason) {
  return reason.replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}
function RouteLegend({
  routes,
  selectedRouteId,
  onRouteSelect,
  hiddenRouteIds = /* @__PURE__ */ new Set(),
  onToggleVisibility
}) {
  if (routes.length === 0) {
    return /* @__PURE__ */ React48.createElement(Card, { className: "h-full" }, /* @__PURE__ */ React48.createElement(CardHeader, { className: "pb-2" }, /* @__PURE__ */ React48.createElement(CardTitle, { className: "text-sm font-medium" }, "Routes")), /* @__PURE__ */ React48.createElement(CardContent, null, /* @__PURE__ */ React48.createElement("p", { className: "text-sm text-muted-foreground" }, "No routes to display")));
  }
  return /* @__PURE__ */ React48.createElement(Card, { className: "h-full flex flex-col" }, /* @__PURE__ */ React48.createElement(CardHeader, { className: "pb-2 flex-shrink-0" }, /* @__PURE__ */ React48.createElement(CardTitle, { className: "text-sm font-medium" }, "Routes (", routes.length, ")")), /* @__PURE__ */ React48.createElement(CardContent, { className: "flex-1 overflow-y-auto px-4 pb-4 pt-0" }, /* @__PURE__ */ React48.createElement("div", { className: "space-y-2" }, routes.map((route, idx) => {
    const color = ROUTE_COLORS[idx % ROUTE_COLORS.length];
    const isSelected = route.taskId === selectedRouteId;
    const isCompleted = route.status === "completed";
    const isHidden = hiddenRouteIds.has(route.taskId);
    return /* @__PURE__ */ React48.createElement(
      "button",
      {
        key: route.taskId,
        onClick: () => onRouteSelect == null ? void 0 : onRouteSelect(isSelected ? null : route.taskId),
        className: `w-full text-left rounded-lg border-2 p-3 transition-all hover:bg-accent/50 ${isHidden ? "opacity-50" : ""} ${isSelected ? "border-primary bg-accent" : "border-transparent"}`
      },
      /* @__PURE__ */ React48.createElement("div", { className: "flex items-center gap-2 mb-2" }, /* @__PURE__ */ React48.createElement(
        "div",
        {
          className: "h-3 w-3 rounded-full flex-shrink-0",
          style: { backgroundColor: color }
        }
      ), /* @__PURE__ */ React48.createElement("span", { className: "font-mono text-xs text-muted-foreground truncate flex-1" }, route.trackingId), isCompleted ? /* @__PURE__ */ React48.createElement(CheckCircle, { className: "h-3.5 w-3.5 text-green-500 flex-shrink-0" }) : route.status === "failed" ? /* @__PURE__ */ React48.createElement(XCircle, { className: "h-3.5 w-3.5 text-red-500 flex-shrink-0" }) : /* @__PURE__ */ React48.createElement(Clock, { className: "h-3.5 w-3.5 text-blue-500 flex-shrink-0 animate-pulse" }), onToggleVisibility && /* @__PURE__ */ React48.createElement(
        "button",
        {
          onClick: (e) => {
            e.stopPropagation();
            onToggleVisibility(route.taskId);
          },
          className: "p-0.5 rounded hover:bg-muted transition-colors flex-shrink-0",
          title: isHidden ? "Show route" : "Hide route"
        },
        isHidden ? /* @__PURE__ */ React48.createElement(EyeOff, { className: "h-4 w-4 text-muted-foreground" }) : /* @__PURE__ */ React48.createElement(Eye, { className: "h-4 w-4 text-muted-foreground" })
      )),
      /* @__PURE__ */ React48.createElement("div", { className: "flex items-start gap-2 mb-1" }, /* @__PURE__ */ React48.createElement(MapPin, { className: "h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" }), /* @__PURE__ */ React48.createElement("div", { className: "min-w-0 flex-1" }, /* @__PURE__ */ React48.createElement("p", { className: "text-sm font-medium truncate" }, route.recipientName), /* @__PURE__ */ React48.createElement("p", { className: "text-xs text-muted-foreground truncate" }, route.recipientAddress))),
      /* @__PURE__ */ React48.createElement("div", { className: "flex items-center justify-between text-xs text-muted-foreground mt-2 gap-2" }, /* @__PURE__ */ React48.createElement("div", { className: "flex items-center gap-1 min-w-0 flex-1" }, /* @__PURE__ */ React48.createElement(User, { className: "h-3 w-3 flex-shrink-0" }), /* @__PURE__ */ React48.createElement("span", { className: "truncate" }, route.driverName)), route.completedAt && /* @__PURE__ */ React48.createElement("span", { className: "flex-shrink-0" }, formatTime(route.completedAt))),
      /* @__PURE__ */ React48.createElement("div", { className: "mt-2 text-xs text-muted-foreground" }, route.totalPoints, " GPS points", route.totalPoints !== route.routePoints.length && /* @__PURE__ */ React48.createElement("span", { className: "ml-1" }, "(", route.routePoints.length, " simplified)")),
      route.status === "failed" && /* @__PURE__ */ React48.createElement("div", { className: "mt-1.5 text-xs text-destructive" }, route.failureNotes ? /* @__PURE__ */ React48.createElement("p", { className: "line-clamp-2" }, route.failureNotes) : route.failureReason ? /* @__PURE__ */ React48.createElement("span", null, formatFailureReason(route.failureReason)) : /* @__PURE__ */ React48.createElement("span", null, "Delivery failed"))
    );
  }))));
}

// src/components/map/HubMapCard.tsx
import React49, { useState as useState6, useEffect as useEffect5 } from "react";
import Map2, { Marker as Marker2, NavigationControl as NavigationControl2 } from "react-map-gl/mapbox";
import { MapPin as MapPin2, Maximize2, X as X3 } from "lucide-react";
import "mapbox-gl/dist/mapbox-gl.css";
function HubMarker({ name }) {
  return /* @__PURE__ */ React49.createElement("div", { className: "flex flex-col items-center -translate-y-1/2" }, /* @__PURE__ */ React49.createElement("div", { className: "bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-medium shadow-md whitespace-nowrap mb-1" }, name), /* @__PURE__ */ React49.createElement("div", { className: "w-8 h-8 rounded-full bg-primary border-2 border-white shadow-lg flex items-center justify-center" }, /* @__PURE__ */ React49.createElement(MapPin2, { className: "h-4 w-4 text-primary-foreground" })));
}
function MapContent({
  mapboxToken,
  location,
  hubName,
  height,
  showControls = false
}) {
  return /* @__PURE__ */ React49.createElement(
    Map2,
    {
      initialViewState: {
        longitude: location.lng,
        latitude: location.lat,
        zoom: 14
      },
      style: { width: "100%", height },
      mapStyle: "mapbox://styles/mapbox/streets-v12",
      mapboxAccessToken: mapboxToken,
      attributionControl: false,
      interactive: showControls
    },
    showControls && /* @__PURE__ */ React49.createElement(NavigationControl2, { position: "top-right" }),
    /* @__PURE__ */ React49.createElement(
      Marker2,
      {
        longitude: location.lng,
        latitude: location.lat,
        anchor: "bottom"
      },
      /* @__PURE__ */ React49.createElement(HubMarker, { name: hubName })
    )
  );
}
async function geocodeAddress(address, mapboxToken) {
  if (!mapboxToken || !address) return null;
  try {
    const encoded = encodeURIComponent(address);
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${mapboxToken}&limit=1`
    );
    if (!response.ok) return null;
    const data = await response.json();
    if (data.features && data.features.length > 0) {
      const [lng, lat] = data.features[0].center;
      return { lat, lng };
    }
  } catch {
  }
  return null;
}
function HubMapCard({ mapboxToken, hubName, address, lat, lng }) {
  const [location, setLocation] = useState6(null);
  const [isLoading, setIsLoading] = useState6(true);
  const [isExpanded, setIsExpanded] = useState6(false);
  useEffect5(() => {
    let cancelled = false;
    async function loadLocation() {
      if (lat && lng) {
        const parsedLat = parseFloat(lat);
        const parsedLng = parseFloat(lng);
        if (!isNaN(parsedLat) && !isNaN(parsedLng)) {
          setLocation({ lat: parsedLat, lng: parsedLng });
          setIsLoading(false);
          return;
        }
      }
      if (!address || !mapboxToken) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      const coords = await geocodeAddress(address, mapboxToken);
      if (!cancelled) {
        setLocation(coords);
        setIsLoading(false);
      }
    }
    loadLocation();
    return () => {
      cancelled = true;
    };
  }, [address, lat, lng, mapboxToken]);
  if (!mapboxToken) {
    return null;
  }
  if (isLoading) {
    return /* @__PURE__ */ React49.createElement(Card, null, /* @__PURE__ */ React49.createElement(CardHeader, { className: "pb-2" }, /* @__PURE__ */ React49.createElement(CardTitle, { className: "text-lg flex items-center gap-2" }, /* @__PURE__ */ React49.createElement(MapPin2, { className: "h-5 w-5" }), "Hub Location")), /* @__PURE__ */ React49.createElement(CardContent, null, /* @__PURE__ */ React49.createElement(Skeleton, { className: "h-40 w-full rounded-lg" }), address && /* @__PURE__ */ React49.createElement("p", { className: "text-sm text-muted-foreground mt-2" }, address)));
  }
  if (!location) {
    return /* @__PURE__ */ React49.createElement(Card, null, /* @__PURE__ */ React49.createElement(CardHeader, { className: "pb-2" }, /* @__PURE__ */ React49.createElement(CardTitle, { className: "text-lg flex items-center gap-2" }, /* @__PURE__ */ React49.createElement(MapPin2, { className: "h-5 w-5" }), "Hub Location")), /* @__PURE__ */ React49.createElement(CardContent, null, /* @__PURE__ */ React49.createElement("div", { className: "flex items-center justify-center h-32 rounded-lg bg-muted" }, /* @__PURE__ */ React49.createElement("div", { className: "text-center text-muted-foreground" }, /* @__PURE__ */ React49.createElement(MapPin2, { className: "h-8 w-8 mx-auto mb-2 opacity-50" }), /* @__PURE__ */ React49.createElement("p", { className: "text-sm" }, "Map unavailable"))), address && /* @__PURE__ */ React49.createElement("p", { className: "text-sm text-muted-foreground mt-2" }, address)));
  }
  return /* @__PURE__ */ React49.createElement(React49.Fragment, null, /* @__PURE__ */ React49.createElement(Card, null, /* @__PURE__ */ React49.createElement(CardHeader, { className: "pb-2" }, /* @__PURE__ */ React49.createElement("div", { className: "flex items-center justify-between" }, /* @__PURE__ */ React49.createElement(CardTitle, { className: "text-lg flex items-center gap-2" }, /* @__PURE__ */ React49.createElement(MapPin2, { className: "h-5 w-5" }), "Hub Location"), /* @__PURE__ */ React49.createElement(
    Button,
    {
      variant: "ghost",
      size: "icon",
      className: "h-8 w-8",
      onClick: () => setIsExpanded(true)
    },
    /* @__PURE__ */ React49.createElement(Maximize2, { className: "h-4 w-4" })
  ))), /* @__PURE__ */ React49.createElement(CardContent, null, /* @__PURE__ */ React49.createElement("div", { className: "rounded-lg overflow-hidden border" }, /* @__PURE__ */ React49.createElement(
    MapContent,
    {
      mapboxToken,
      location,
      hubName,
      height: "160px",
      showControls: false
    }
  )), address && /* @__PURE__ */ React49.createElement("p", { className: "text-sm text-muted-foreground mt-2" }, address))), /* @__PURE__ */ React49.createElement(Dialog, { open: isExpanded, onOpenChange: setIsExpanded }, /* @__PURE__ */ React49.createElement(DialogContent, { className: "max-w-3xl p-0 overflow-hidden" }, /* @__PURE__ */ React49.createElement("div", { className: "relative" }, /* @__PURE__ */ React49.createElement(
    Button,
    {
      variant: "ghost",
      size: "icon",
      className: "absolute top-2 right-2 z-10 bg-background/80 backdrop-blur-sm",
      onClick: () => setIsExpanded(false)
    },
    /* @__PURE__ */ React49.createElement(X3, { className: "h-4 w-4" })
  ), /* @__PURE__ */ React49.createElement(
    MapContent,
    {
      mapboxToken,
      location,
      hubName,
      height: "500px",
      showControls: true
    }
  )))));
}

// src/components/map/LocationMap.tsx
import React50, { useState as useState7, useEffect as useEffect6 } from "react";
import Map3, { Marker as Marker3, NavigationControl as NavigationControl3 } from "react-map-gl/mapbox";
import { MapPin as MapPin3, Maximize2 as Maximize22, X as X4 } from "lucide-react";
import "mapbox-gl/dist/mapbox-gl.css";
function LocationMarker({ name }) {
  return /* @__PURE__ */ React50.createElement("div", { className: "flex flex-col items-center -translate-y-1/2" }, /* @__PURE__ */ React50.createElement("div", { className: "bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-medium shadow-md whitespace-nowrap mb-1" }, name), /* @__PURE__ */ React50.createElement("div", { className: "w-8 h-8 rounded-full bg-primary border-2 border-white shadow-lg flex items-center justify-center" }, /* @__PURE__ */ React50.createElement(MapPin3, { className: "h-4 w-4 text-primary-foreground" })));
}
function MapContent2({
  mapboxToken,
  location,
  locationName,
  height,
  showControls = false
}) {
  return /* @__PURE__ */ React50.createElement(
    Map3,
    {
      initialViewState: {
        longitude: location.lng,
        latitude: location.lat,
        zoom: 14
      },
      style: { width: "100%", height },
      mapStyle: "mapbox://styles/mapbox/streets-v12",
      mapboxAccessToken: mapboxToken,
      attributionControl: false,
      interactive: showControls
    },
    showControls && /* @__PURE__ */ React50.createElement(NavigationControl3, { position: "top-right" }),
    /* @__PURE__ */ React50.createElement(
      Marker3,
      {
        longitude: location.lng,
        latitude: location.lat,
        anchor: "bottom"
      },
      /* @__PURE__ */ React50.createElement(LocationMarker, { name: locationName })
    )
  );
}
async function geocodeAddress2(address, mapboxToken) {
  if (!mapboxToken || !address) return null;
  try {
    const encoded = encodeURIComponent(address);
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${mapboxToken}&limit=1`
    );
    if (!response.ok) return null;
    const data = await response.json();
    if (data.features && data.features.length > 0) {
      const [lng, lat] = data.features[0].center;
      return { lat, lng };
    }
  } catch {
  }
  return null;
}
function LocationMap({
  mapboxToken,
  locationName,
  address,
  lat,
  lng,
  height = "200px",
  expandable = true
}) {
  const [location, setLocation] = useState7(null);
  const [isLoading, setIsLoading] = useState7(true);
  const [isExpanded, setIsExpanded] = useState7(false);
  useEffect6(() => {
    let cancelled = false;
    async function loadLocation() {
      if (lat && lng) {
        const parsedLat = parseFloat(lat);
        const parsedLng = parseFloat(lng);
        if (!isNaN(parsedLat) && !isNaN(parsedLng)) {
          setLocation({ lat: parsedLat, lng: parsedLng });
          setIsLoading(false);
          return;
        }
      }
      if (!address || !mapboxToken) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      const coords = await geocodeAddress2(address, mapboxToken);
      if (!cancelled) {
        setLocation(coords);
        setIsLoading(false);
      }
    }
    loadLocation();
    return () => {
      cancelled = true;
    };
  }, [address, lat, lng, mapboxToken]);
  if (!mapboxToken) {
    return null;
  }
  if (isLoading) {
    return /* @__PURE__ */ React50.createElement(Skeleton, { className: "w-full rounded-lg", style: { height } });
  }
  if (!location) {
    return /* @__PURE__ */ React50.createElement(
      "div",
      {
        className: "flex items-center justify-center rounded-lg bg-muted border",
        style: { height }
      },
      /* @__PURE__ */ React50.createElement("div", { className: "text-center text-muted-foreground" }, /* @__PURE__ */ React50.createElement(MapPin3, { className: "h-8 w-8 mx-auto mb-2 opacity-50" }), /* @__PURE__ */ React50.createElement("p", { className: "text-sm" }, "Map unavailable"))
    );
  }
  return /* @__PURE__ */ React50.createElement(React50.Fragment, null, /* @__PURE__ */ React50.createElement("div", { className: "relative rounded-lg overflow-hidden border" }, /* @__PURE__ */ React50.createElement(
    MapContent2,
    {
      mapboxToken,
      location,
      locationName,
      height,
      showControls: false
    }
  ), expandable && /* @__PURE__ */ React50.createElement(
    Button,
    {
      variant: "secondary",
      size: "icon",
      className: "absolute top-2 right-2 h-8 w-8 bg-background/80 backdrop-blur-sm",
      onClick: () => setIsExpanded(true)
    },
    /* @__PURE__ */ React50.createElement(Maximize22, { className: "h-4 w-4" })
  )), expandable && /* @__PURE__ */ React50.createElement(Dialog, { open: isExpanded, onOpenChange: setIsExpanded }, /* @__PURE__ */ React50.createElement(DialogContent, { className: "max-w-3xl p-0 overflow-hidden" }, /* @__PURE__ */ React50.createElement("div", { className: "relative" }, /* @__PURE__ */ React50.createElement(
    Button,
    {
      variant: "ghost",
      size: "icon",
      className: "absolute top-2 right-2 z-10 bg-background/80 backdrop-blur-sm",
      onClick: () => setIsExpanded(false)
    },
    /* @__PURE__ */ React50.createElement(X4, { className: "h-4 w-4" })
  ), /* @__PURE__ */ React50.createElement(
    MapContent2,
    {
      mapboxToken,
      location,
      locationName,
      height: "500px",
      showControls: true
    }
  )))));
}

export {
  cn,
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Alert,
  AlertTitle,
  AlertDescription,
  InitialsAvatar,
  AvatarStack,
  buttonVariants,
  Button,
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
  badgeVariants,
  Badge,
  Calendar,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Checkbox,
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogClose,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Drawer,
  DrawerTrigger,
  DrawerPortal,
  DrawerClose,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuRadioGroup,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  Input,
  ScrollArea,
  ScrollBar,
  DualListbox,
  Label2 as Label,
  Controller,
  FormProvider,
  useFormContext,
  useForm,
  zodResolver,
  Form,
  FormField,
  useFormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  navigationMenuTriggerStyle,
  NavigationMenuTrigger,
  NavigationMenuContent,
  NavigationMenuLink,
  NavigationMenuViewport,
  NavigationMenuIndicator,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
  Popover,
  PopoverTrigger,
  PopoverPortal,
  PopoverContent,
  Progress,
  RadioGroup2 as RadioGroup,
  RadioGroupItem,
  SearchableSelect,
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectScrollUpButton,
  SelectScrollDownButton,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  useSelectOrCreate,
  SelectOrCreate,
  Separator3 as Separator,
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetPortal,
  SheetOverlay,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
  Skeleton,
  Slider,
  Switch,
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableRow,
  TableHead,
  TableCell,
  TableCaption,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Textarea,
  toggleVariants,
  Toggle,
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  QuantityCounter,
  SmartPrice,
  StockBadge,
  getStockStatus,
  ProductCard,
  BrandCard,
  ProductGrid,
  ROUTE_COLORS,
  RouteMap,
  RouteLegend,
  HubMapCard,
  LocationMap,
  VisuallyHidden
};
